import type { ServiceAreaFaq, ServiceAreaSlug } from "@/config/service-areas";
import type { ServicePage } from "@/config/services";

type ServiceSlug =
  | "emergency-roof-leak-repair"
  | "flashing-roof-penetration-repair"
  | "moss-removal-roof-gutter"
  | "asphalt-shingle-replacement"
  | "roof-health-check-maintenance"
  | "shingle-spot-repair"
  | "gutter-cleaning-downspout-flush"
  | "storm-tarping-emergency-dry-in"
  | "skylight-leak-flashing-repair"
  | "chimney-flashing-repair"
  | "gutter-replacement"
  | "gutter-guard-installation"
  | "roof-ventilation-upgrade"
  | "fascia-soffit-repair-replacement"
  | "cedar-shake-repair-restoration"
  | "flat-low-slope-roof-repair"
  | "metal-roof-repair-maintenance"
  | "cedar-shake-roof-replacement"
  | "metal-roof-replacement"
  | "flat-roof-replacement-sbs-tpo-pvc"
  | "roof-replacement"
  | "roof-repair"
  | "asphalt-shingle-roofing"
  | "metal-roofing"
  | "flat-roofing"
  | "roof-inspection-maintenance";

type ServiceContent = Partial<Omit<ServicePage, "slug" | "image">>;
type ServiceAreaContent = {
  intro: string;
  localContext: string;
  assessmentFocus: readonly string[];
  propertyContexts: readonly string[];
  faqs: readonly ServiceAreaFaq[];
};

export const serviceContent = {
  "roof-replacement": {
    title: "沥青瓦屋顶更换",
    shortTitle: "沥青瓦更换",
    eyebrow: "陡坡重铺",
    description: "为大温哥华陡坡住宅提供完整的沥青瓦拆除、垫层、瓦片、泛水整合及废料清运。",
    definition:
      "沥青瓦屋顶更换是一次完整的陡坡重铺：拆除现有瓦面，处理基层和垫层需求，安装新的瓦片系统，并重新整合泛水和穿透点。",
    forWhom: "瓦片已大范围磨损、反复维修，或不再符合住宅剩余使用计划的业主。",
    warningSigns: [
      "瓦片卷曲、开裂或大面积颗粒脱落",
      "多个已维修屋面仍反复失效",
      "瓦片变脆、无法再重新密封",
      "天沟、屋脊和檐口老化",
      "翻修工程使整体重铺成为更合理的选择",
    ],
    evaluates: [
      "现有层数和屋面板状况",
      "坡度、天沟、老虎窗和几何形态",
      "通风及阁楼受潮迹象",
      "泛水和穿透点清单",
      "进场、保护和废料清运",
    ],
    causes: [
      "正常使用寿命末期风化",
      "长期苔藓和潮湿",
      "累积的风损",
      "通风不良加速老化",
      "以往安装细部薄弱",
    ],
    methodology: [
      "记录状况和可选方案",
      "规划拆除、保护和清运",
      "按发现情况维修屋面板并书面说明",
      "按顺序安装垫层、泛水和现场瓦片",
      "将屋脊、天沟、边缘和穿透点作为完整组件细部处理",
    ],
    materials: [
      "建筑型沥青瓦",
      "垫层及在适当位置的冰坝/防水膜",
      "天沟、墙体和穿透点泛水",
      "屋脊和通风配件",
      "紧固件和边缘构件",
    ],
    bcClimate: "沿海降雨和苔藓生长使垫层、天沟细部和通风在大温地区沥青瓦屋顶上尤为重要。",
    whenAppropriate: "当瓦片老化具有系统性，或继续局部修补仍会留下其他近期失效风险时。",
    whenOtherService: "若周边系统仍可正常使用，孤立风损可能仍适合局部维修。",
    solutionCategories: [
      {
        title: "建筑型复合沥青瓦",
        body: "根据几何形态、暴露条件和设计意图选择的立体住宅瓦型。",
      },
      {
        title: "垫层与排水细部",
        body: "协调现场瓦片与天沟、檐口和过渡部位。",
      },
      {
        title: "泛水重新整合",
        body: "墙体、烟囱、通风口和边缘作为新组件的一部分进行细部处理。",
      },
    ],
    repairVsReplace: {
      repair: "当损坏局限且匹配材料能干净整合时，局部维修可行。",
      replace: "当磨损广泛或组件无法接受耐久补丁时，更换更为合理。",
    },
    scopeFactors: [
      "屋顶面积和坡度",
      "层数",
      "屋面板维修",
      "天沟和老虎窗复杂程度",
      "材料选择",
      "进场和清运",
    ],
    process: [
      "评估覆盖层、屋面板指标和通风",
      "书面工程范围，含材料和细部选项",
      "拆除并做好物业保护和废料清运",
      "按顺序进行屋面板维修、垫层、泛水和瓦片安装",
      "最终巡查和完工复核",
    ],
    publicPriceHint:
      "项目价格取决于屋顶面积、坡度、层数、屋面板维修、天沟和老虎窗复杂程度及材料选择。请申请评估以获取书面工程范围。",
    faqs: [
      {
        question: "什么是沥青瓦屋顶更换？",
        answer:
          "沥青瓦屋顶更换是一次完整的陡坡重铺：拆除现有瓦面，处理基层和垫层需求，安装新的瓦片系统，并重新整合泛水和穿透点。",
      },
      {
        question: "沥青瓦屋顶更换需要多长时间？",
        answer:
          "大多数独立住宅重铺以天计而非以周计，但工期取决于屋顶大小、坡度、天气窗口，以及拆除过程中发现的屋面板维修量。书面工程范围会在开工前说明预期。",
      },
      {
        question: "可以在旧瓦片上直接铺新瓦吗？",
        answer:
          "叠铺有时在技术上可行，但通常更倾向拆除：这样可以检查屋面板、正确安装垫层和泛水，并避免增加重量、把问题困在新覆盖层下方。",
      },
      {
        question: "拆除时发现腐烂屋面板怎么办？",
        answer:
          "发现的屋面板损坏会在新系统安装前记录并维修。清晰的工程范围会事先说明屋面板工作如何计量和定价，使中途发现透明处理，而非意外追加。",
      },
      {
        question: "哪种瓦片最适合大温哥华天气？",
        answer:
          "建筑型复合沥青瓦是当地常见的住宅选择。在此气候下，垫层质量、天沟和边缘细部以及阁楼通风，对实际使用寿命的影响往往不亚于瓦片产品本身。",
      },
    ],
  },
  "roof-repair": {
    title: "紧急屋顶漏水维修",
    shortTitle: "紧急漏水维修",
    eyebrow: "主动进水",
    description:
      "优先漏水诊断：在排期允许时当日到场，针对水从大温哥华住宅屋顶系统进入的情况，进行临时或永久性局部维修。",
    definition:
      "紧急屋顶漏水维修是针对屋顶覆盖层、泛水、穿透点或相关接口处主动或快速发展的进水，进行重点排查与稳定处理。在条件允许时，工作可包括临时防水和永久性局部维修。",
    forWhom:
      "出现持续滴水、雨天突发天花水渍、暴风雨后阁楼受潮，或需要在规划更大范围工程前进行安全诊断和控制的业主。",
    warningSigns: [
      "降雨期间或之后持续滴水或潮湿",
      "天花、墙体或灯具上出现新的或扩大的水渍",
      "保温层、屋面板或阁楼构架受潮",
      "水沿天沟、烟囱、天窗或管道通风口蔓延",
      "每次天气事件后室内损坏持续加重",
    ],
    evaluates: [
      "室内证据及可能的水流路径",
      "受影响区域周边的屋面覆盖层",
      "泛水、穿透点、天沟和墙体接口",
      "临时保护还是永久维修的需求",
      "相邻材料能否支持耐久施工",
    ],
    causes: [
      "泛水或管道护套密封失效",
      "被风掀起或缺失的覆盖材料",
      "杂物堵塞排水导致积水",
      "暴风雨损坏屋脊、边缘或过渡部位",
      "以往修补未处理真正源头",
    ],
    methodology: [
      "安全记录室内和室外证据",
      "追查最可能的进水路径，而不只是水渍位置",
      "在天气或进场条件需要时以临时防水稳定",
      "在周边组件仍可使用时，以兼容材料进行局部永久维修",
      "说明残余风险及是否应进行更大范围工程",
    ],
    materials: [
      "与现有系统兼容的覆盖材料",
      "在适当位置的垫层或膜材补丁",
      "泛水金属件和穿透护套",
      "密封胶仅作为正确细部的一部分，而非唯一修复",
      "需要时的临时防水布或干铺材料",
    ],
    bcClimate:
      "大温哥华频繁降雨意味着小缺陷也会反复造成室内损坏。及时诊断比表面修补、却未解决真正路径更为重要。",
    whenAppropriate: "当进水局限、周边屋顶仍可正常使用，且能安全执行耐久维修或临时防水时。",
    whenOtherService:
      "覆盖层大范围失效、多处独立漏水，或屋顶系统已接近末期时，可能需要更广泛的维修规划、仅做暴风雨临时防水，或讨论整体更换。",
    solutionCategories: [
      {
        title: "源头诊断",
        body: "将室内证据与最可能进水的室外细部联系起来，包括覆盖层下方的横向水流。",
      },
      {
        title: "临时防水",
        body: "在暴风雨损坏后，或永久材料无法在同一次上门完成时，稳定开口部位。",
      },
      {
        title: "局部永久维修",
        body: "以与周边屋顶系统兼容的材料，更换或重新细部处理失效构件。",
      },
    ],
    repairVsReplace: {
      repair: "当已确定单一失效区域且周边材料仍足够完好、可接受兼容施工时，通常适合重点维修。",
      replace: "当检查发现大范围老化、多处独立失效，或组件无法支持耐久补丁时，更换会进入讨论。",
    },
    scopeFactors: [
      "进场条件和屋顶高度",
      "天气窗口",
      "隐蔽损坏范围",
      "材料兼容性",
      "涉及的穿透点数量",
      "临时保护需求",
    ],
    process: [
      "致电或发送您所观察到的情况及照片",
      "分诊与排期：在有空档时当日到场",
      "在屋顶上溯源诊断，并记录室内和室外情况",
      "按条件允许进行临时防水或局部永久维修",
      "书面总结发现、已完成工作及建议的后续步骤",
    ],
    publicPriceHint:
      "紧急工程范围取决于进场条件、施工时的天气、水已蔓延的距离，以及永久维修前是否需要临时保护。请申请评估以获取针对具体项目的报价。",
    faqs: [
      {
        question: "什么是紧急屋顶漏水维修？",
        answer:
          "紧急屋顶漏水维修是针对屋顶覆盖层、泛水、穿透点或相关接口处主动或快速发展的进水，进行重点排查与稳定处理。在条件允许时，工作可包括临时防水和永久性局部维修。",
      },
      {
        question: "下雨时还能修屋顶漏水吗？",
        answer:
          "在持续降雨时，安全的第一步通常是临时防水：铺防水布或密封进水区域，因为大多数永久屋面材料需要干燥条件才能正确粘结。永久维修随后在合适的天气窗口完成。",
      },
      {
        question: "为什么天花水渍离可见屋顶损坏很远？",
        answer:
          "水常从一个细部进入，再沿垫层、结构构件或屋面板移动后才在室内显现。因此诊断会追查屋顶上可能的进水路径，而不只是直接修补水渍上方。",
      },
      {
        question: "你们在大温哥华提供当日屋顶漏水维修吗？",
        answer:
          "Revolin 会在排期和日光条件允许时，尽量当日处理主动漏水，但到场时间不作保证。分享照片和对所见情况的清晰描述，有助于分诊沟通。",
      },
      {
        question: "漏水维修会是临时的还是永久的？",
        answer:
          "这取决于天气、材料供应和排查结果。若首次上门能安全完成耐久的局部维修，就会这样做；否则先用临时防水稳定屋顶，再以书面形式确定永久工程范围。",
      },
      {
        question: "紧急屋顶漏水维修费用是多少？",
        answer:
          "价格取决于屋顶高度和进场条件、隐蔽损坏范围，以及除维修外是否还需要临时保护。Revolin 在评估后提供针对具体项目的报价，而不是未经现场查看就报价。",
      },
    ],
  },
  "asphalt-shingle-roofing": {
    title: "建筑型沥青瓦屋顶",
    shortTitle: "沥青瓦",
    eyebrow: "陡坡屋顶系统",
    description: "适用于大温哥华许多具备相应陡坡屋顶系统住宅的实用、兼顾设计的屋顶类别。",
    forWhom:
      "建筑型沥青瓦适合许多坡屋顶住宅，满足业主对成熟系统、丰富外观选择及日后便于维护的需求。",
    warningSigns: [
      "颗粒脱落或胎基外露",
      "瓦片卷曲、开裂或移位",
      "天沟、边缘或穿透点周边损坏",
      "苔藓遮盖屋面",
      "多个屋面反复维修",
    ],
    evaluates: [
      "屋顶坡度和几何形态",
      "经适当检查后的屋面板状况",
      "垫层、檐口和天沟做法",
      "通风及进、排气路径",
      "泛水接口与排水",
    ],
    solutionCategories: [
      {
        title: "建筑型复合沥青瓦",
        body: "广泛用于住宅陡坡屋顶的立体瓦型，具体选择取决于系统和设计要求。",
      },
      {
        title: "垫层与排水细部",
        body: "可见瓦片只是协调系统的一层，完整系统还包括搭接、天沟、边缘和交接部位。",
      },
      {
        title: "泛水整合",
        body: "墙体、烟囱、管道通风口和屋顶构件的接口，需要与瓦面按明确顺序施工。",
      },
    ],
    repairVsReplace: {
      repair: "若能在不损坏脆弱周边瓦面的情况下整合匹配且兼容的瓦片，局部维修可能可行。",
      replace: "若瓦片普遍变脆、磨损或多个屋面均受影响，更换可能更合适。",
    },
    scopeFactors: [
      "屋顶坡度和总面积",
      "天沟、老虎窗和穿透点数量",
      "现有层数及屋面板维修",
      "瓦型和配件选择",
      "通风和泛水工程",
      "进场和现场保护",
    ],
    faqs: [
      {
        question: "建筑型沥青瓦适合所有坡屋顶吗？",
        answer:
          "不适合。必须考虑坡度、基层、暴露条件、几何形态和当地要求。选择产品前，应将屋顶作为完整系统进行评估。",
      },
      {
        question: "颜色会影响建筑效果吗？",
        answer:
          "会。屋顶颜色会与外墙、饰边、建筑体量及街道可见度相互作用。应结合实际建筑查看样品，而不应只看屏幕。",
      },
    ],
  },
  "metal-roofing": {
    title: "金属屋顶更换",
    shortTitle: "金属屋顶更换",
    eyebrow: "高端金属重铺",
    description: "为适合的陡坡建筑提供直立锁边或其他高端金属重铺。",
    definition:
      "金属屋顶更换是在评估几何形态、基层、垫层和建筑意图后，安装新的金属屋面系统：通常为直立锁边系统。",
    forWhom: "为适合的几何形态、建筑风格和长期规划，选择高端金属系统的业主。",
    warningSigns: [
      "现有覆盖层已至末期且选定金属作为新系统",
      "建筑适合直立锁边",
      "希望获得高端覆盖层外观",
      "复杂几何形态更适合金属板表达",
    ],
    evaluates: ["型材选择", "垫层和基层", "防冷凝方案", "泛水和穿透点", "声学和设计预期"],
    causes: ["计划中的系统升级", "现有屋顶使用寿命末期", "设计翻修", "与金属组件相符的性能目标"],
    methodology: [
      "确认几何形态适用性",
      "确定板材系统和垫层",
      "准备基层",
      "将板材和泛水作为完整系统安装",
      "有意细部处理屋脊、边缘和穿透点",
    ],
    materials: ["直立锁边或指定金属板", "垫层系统", "系统所需的夹具/紧固件", "泛水和收口件"],
    bcClimate:
      "西海岸建筑常与直立锁边金属相得益彰，但在潮湿天气中，垫层、通风和细部仍决定实际性能。",
    whenAppropriate: "当金属为选定系统且项目范围为完整重铺时。",
    whenOtherService: "若现有金属屋顶整体仍完好，构件级维修可能仍适用。",
    solutionCategories: [
      {
        title: "直立锁边系统",
        body: "在指定时采用隐藏式固定的高端板型外观。",
      },
      {
        title: "垫层与基层准备",
        body: "在金属下方构建排水基础层。",
      },
      {
        title: "建筑细部",
        body: "边缘、天沟和墙体细部兼顾性能与外观。",
      },
    ],
    repairVsReplace: {
      repair: "当板材仍完好时维修现有金属屋顶。",
      replace: "当新项目目标是新金属系统，或旧覆盖层已完工时进行更换。",
    },
    scopeFactors: ["屋顶面积和几何形态", "板材型材", "垫层选择", "进场条件", "穿透点复杂程度"],
    process: [
      "确认几何形态和系统适用性",
      "确定板材型材、垫层和细部",
      "书面工程范围，记录设计决策",
      "基层准备和板材安装",
      "细部复核和完工巡查",
    ],
    publicPriceHint:
      "金属重铺价格取决于屋顶几何形态、板材型材、垫层策略和穿透点复杂程度。请申请评估以获取书面工程范围。",
    faqs: [
      {
        question: "什么是金属屋顶更换？",
        answer:
          "金属屋顶更换是在评估几何形态、基层、垫层和建筑意图后，安装新的金属屋面系统：通常为直立锁边系统。",
      },
      {
        question: "金属屋顶下雨会很吵吗？",
        answer:
          "在实心屋面板、垫层和保温下方的住宅重铺上，雨声通常远小于多数人预期：响亮印象多来自架空框架上的板材。系统选择时仍会讨论声学预期。",
      },
      {
        question: "直立锁边和明钉金属有什么区别？",
        answer:
          "直立锁边通过凸起接缝下方的夹具隐藏固定，允许板材热胀冷缩并将垫圈螺钉置于天气之外。明钉板成本较低，但需长期维护紧固件。高端住宅工程通常推荐直立锁边。",
      },
      {
        question: "可以在现有瓦片上直接铺金属屋顶吗？",
        answer:
          "有时结构上可行，但通常更倾向拆除，以便检查屋面板并正确建造垫层和泛水系统。困住旧覆盖层也会使日后诊断更困难。",
      },
      {
        question: "金属屋顶适合复杂屋顶造型吗？",
        answer:
          "适合，但复杂程度决定金属项目的成败：屋脊、天沟、老虎窗和死胡同细部需要熟练的金属工艺并增加成本。几何复核是判断金属是否适合住宅的第一步。",
      },
    ],
  },
  "flat-roofing": {
    title: "平屋顶更换：SBS / TPO / PVC",
    shortTitle: "平屋顶更换",
    eyebrow: "低坡重铺",
    description: "使用 SBS、TPO、PVC 或其他指定膜材系统，完成住宅或商业低坡屋顶的整体重铺。",
    definition:
      "平屋顶更换拆除或覆盖现有低坡屋顶组件，并安装根据排水、基层和项目要求选定的新膜材系统。",
    forWhom: "低坡屋顶出现反复膜材失效、保温受潮、系统过时，或配合外部翻修的业主。",
    warningSigns: [
      "多处膜材失效",
      "保温层受潮",
      "系统过时或无法维护",
      "配合外部翻修工程",
      "长期积水且系统整体磨损",
    ],
    evaluates: ["膜材类型选项", "保温和基层需求", "排水口和泄水口", "女儿墙和穿透点", "进场和保护"],
    causes: [
      "膜材使用寿命末期",
      "保温饱和",
      "排水设计局限",
      "紫外线和热老化",
      "穿透点密集且细部失效累积",
    ],
    methodology: [
      "评估组件，在需要时调查受潮部位",
      "为项目选定膜材策略",
      "按范围拆除或覆盖",
      "按顺序安装保温和膜材层",
      "将排水口、女儿墙和穿透点作为系统细部处理",
    ],
    materials: [
      "SBS、TPO、PVC 或其他指定膜材",
      "按范围的保温和盖板",
      "排水和泛水配件",
      "与系统相符的胶粘剂、紧固件或热工工艺",
    ],
    bcClimate: "大温哥华多雨环境中，低坡屋顶更依赖排水设计和稳健的膜材细部，而非营销标签本身。",
    whenAppropriate: "当低坡组件已耗尽，或作为更广泛项目的一部分进行更新时。",
    whenOtherService: "若膜材现场仍完好，局部膜材维修可能仍可行。",
    solutionCategories: [
      {
        title: "膜材系统选择",
        body: "根据项目条件选择 SBS、TPO、PVC 或其他指定组件。",
      },
      {
        title: "组件更换",
        body: "按批准范围处理膜材及相关层次。",
      },
      {
        title: "排水与细部",
        body: "排水口、女儿墙和穿透点作为核心防水工程处理。",
      },
    ],
    repairVsReplace: {
      repair: "若膜材现场仍可使用则进行维修。",
      replace: "当系统已耗尽时进行完整低坡更换。",
    },
    scopeFactors: ["面积和几何形态", "膜材类型", "保温需求", "穿透点密度", "进场和清运"],
    process: [
      "组件评估，在需要时进行受潮调查",
      "膜材系统选择和书面工程范围",
      "按范围拆除或覆盖现有组件",
      "按顺序安装保温和膜材",
      "排水口、女儿墙和穿透点细部复核",
    ],
    publicPriceHint:
      "低坡更换价格取决于面积、膜材选择、保温需求、排水工程和穿透点密度。请申请评估。",
    faqs: [
      {
        question: "使用 SBS、TPO 或 PVC 的平屋顶更换是什么？",
        answer:
          "平屋顶更换拆除或覆盖现有低坡屋顶组件，并安装根据排水、基层和项目要求选定的新膜材系统。",
      },
      {
        question: "SBS、TPO 还是 PVC，哪种平屋顶膜材最好？",
        answer:
          "没有一种普遍适用于所有情况的最佳选择。双层 SBS 是当地常用、冗余度高的系统；TPO 和 PVC 为热焊单层系统，重量较轻并有反射选项，PVC 还具有较强的耐化学性。基层、排水、屋顶使用方式和预算决定选择。",
      },
      {
        question: "应该拆除还是覆盖现有平屋顶？",
        answer:
          "若现有组件干燥完好，覆盖可节省成本和清运；但若保温受潮或已有多个层次，拆除是更诚实的路径。决策前的受潮调查可避免把问题封在里面。",
      },
      {
        question: "平屋顶更换有多大干扰？",
        answer:
          "预计会有噪音、热熔或胶粘作业时的气味、分阶段材料上料和受保护的通道。住户通常可以留在家中，书面工程范围会事先说明工序、工作时间和保护措施。",
      },
      {
        question: "平屋顶更换时能改善排水吗？",
        answer:
          "更换是纠正长期积水的合适时机：锥形保温、增设或移位排水口以及泄水口工程，可以设计进新组件，而不是事后补救。",
      },
    ],
  },
  "roof-inspection-maintenance": {
    title: "屋顶健康检查与维护",
    shortTitle: "健康检查",
    eyebrow: "先看状况",
    description: "检查、拍照、少量密封胶或维护工作，以及供规划参考的屋顶状况报告。",
    definition:
      "屋顶健康检查是对覆盖层、泛水、排水及可接近相关组件的结构化目视评估，通常配合照片记录和事先约定的有限维护项目。",
    forWhom: "准备买卖、计划翻修、注意到早期警示迹象，或希望在雨季前建立有记录基线的业主。",
    warningSigns: [
      "不确定屋顶年龄或剩余状况",
      "轻微水渍但来源不明",
      "季节性杂物和苔藓积聚",
      "穿透点密封胶老化",
      "需要有记录的计划基线",
    ],
    evaluates: [
      "按屋面划分的覆盖层状况",
      "泛水和穿透点",
      "排水和天沟接口",
      "可见通风组件",
      "维护与维修事项的优先排序",
    ],
    causes: [
      "正常老化但缺乏记录",
      "维护拖延",
      "场地特定的天气暴露",
      "质量不明的以往工程",
      "买卖或翻修决策需求",
    ],
    methodology: [
      "了解关注点和可获取的历史",
      "系统检查外部屋顶区域",
      "拍摄代表性状况照片",
      "仅完成约定的少量维护",
      "提供清晰的状况摘要和选项",
    ],
    materials: [
      "照片记录",
      "包含时的少量密封胶或紧固件",
      "若纳入范围则清理有限杂物",
      "书面状况说明",
    ],
    bcClimate: "季节性降雨使入冬前和暴风雨后的健康检查有助于发现堵塞排水、材料翘起和早期泛水失效。",
    whenAppropriate: "在承诺维修、更换或进一步调查前需要清晰了解状况时。",
    whenOtherService: "主动严重漏水或暴风雨开口应走紧急或维修路径。",
    solutionCategories: [
      {
        title: "有记录的检查",
        body: "对屋顶组件上可见且可接近部分的结构化审阅。",
      },
      {
        title: "少量维护",
        body: "仅在纳入范围且适当时进行的有限密封胶或杂物工作。",
      },
      {
        title: "规划摘要",
        body: "区分需立即处理的事项与中期监测事项。",
      },
    ],
    repairVsReplace: {
      repair: "许多发现是维护或局部维修事项，而非全面更换的触发因素。",
      replace: "仅当观察到的状况支持更广泛路径时才讨论更换。",
    },
    scopeFactors: [
      "屋顶大小和复杂程度",
      "进场条件",
      "照片记录深度",
      "包含的维护项目",
      "物业上的屋顶系统数量",
    ],
    process: [
      "预约健康检查并说明任何关注点",
      "系统性外部检查并拍照记录",
      "上门期间完成约定的少量维护",
      "书面状况摘要及排序后的优先事项",
      "就建议的维修进行后续讨论",
    ],
    publicPriceHint:
      "健康检查范围取决于屋顶大小和复杂程度、进场条件，以及包含多少记录和少量维护。请申请评估。",
    faqs: [
      {
        question: "什么是屋顶健康检查与维护？",
        answer:
          "屋顶健康检查是对覆盖层、泛水、排水及可接近相关组件的结构化目视评估，通常配合照片记录和事先约定的有限维护项目。",
      },
      {
        question: "我应该多久检查一次屋顶？",
        answer:
          "当地较有用的节奏是秋季降雨前检查一次，并在重大风暴后再查。树木覆盖重的住宅，或屋顶已进入预期寿命后半段的住宅，值得更频繁关注。",
      },
      {
        question: "健康检查后我会收到什么？",
        answer:
          "附有照片的覆盖层、泛水、排水和通风状况摘要，区分需及时维修的事项、需监测的事项和正常磨损。这是一份可按自己节奏采取行动的规划文件。",
      },
      {
        question: "买卖房屋前做屋顶检查值得吗？",
        answer:
          "值得：屋顶是较昂贵的建筑组件之一，有记录的状况基线能减少谈判中的猜测。请注意这是屋顶状况审阅，而非正式的购房验房。",
      },
      {
        question: "检查时发现的问题会当场修吗？",
        answer:
          "事先约定的小维护：例如穿透点少量密封胶更新，可在上门时完成。较大发现会记录并单独确定范围，让您凭清晰信息做决定，而非现场施压。",
      },
    ],
  },
  "flashing-roof-penetration-repair": {
    title: "泛水 / 屋顶穿透点维修",
    shortTitle: "泛水与穿透点",
    eyebrow: "过渡与接口",
    description:
      "维修烟囱、墙体、通风口、管道护套及其他屋顶穿透点泛水：这些是大温哥华屋顶常见的进水位置。",
    definition:
      "泛水和屋顶穿透点维修修正屋顶覆盖层与墙体、烟囱、通风口、管道及其他穿透点之间的导水接口。金属件、护套或密封胶接缝失效时，这些细部是频繁的漏水来源。",
    forWhom:
      "在烟囱、侧墙、管道立管、机械通风口或其他屋顶开口附近发现漏水，或泛水老化、已无法正确导水的业主。",
    warningSigns: [
      "烟囱井道或外墙附近出现水渍",
      "穿透点密封胶开裂或硬化",
      "step flashing 或 counter-flashing 翘起、生锈或分离",
      "橡胶管道护套老化破裂",
      "墙体与屋顶交接处在风雨天进水",
    ],
    evaluates: [
      "泛水几何形态和材料状况",
      "与周边屋面覆盖层的兼容性",
      "counter-flashing 和墙体接口完整性",
      "穿透护套和套环状况",
      "密封胶是否在掩盖失效的金属细部",
    ],
    causes: [
      "热胀冷缩使接缝张开",
      "紫外线和天气使护套、密封胶老化",
      "step 或 counter-flashing 整合不当",
      "泛水金属腐蚀或机械损坏",
      "更换覆盖层时未正确重做穿透点细部",
    ],
    methodology: [
      "识别失效区域内的每个穿透点和墙体接口",
      "仅在需要时打开以确认水流路径",
      "以兼容细部更换失效护套、套环或金属件",
      "将泛水正确重新整合进现场覆盖层",
      "避免仅依赖表面填缝作为永久防水策略",
    ],
    materials: [
      "step、counter 和 apron 泛水",
      "管道护套和通风套环",
      "关键接口处兼容的垫层和冰坝/防水膜",
      "适合沿海暴露及相邻材料的金属",
      "制造商兼容的密封胶仅作次要保护",
    ],
    bcClimate:
      "风雨交加和长湿季给泛水和弹性护套带来压力。在干燥气候有效的细部，若金属整合不正确，在 BC 沿海往往更早失效。",
    whenAppropriate: "当现场覆盖层仍可正常使用，且漏水由局部泛水或穿透点失效引起时。",
    whenOtherService:
      "若周边覆盖层已大范围磨损，或烟囱、天窗、低坡组件需要不同范围，可能更适合专项服务。",
    solutionCategories: [
      {
        title: "穿透护套更换",
        body: "在橡胶或通风护套开裂进水前更换老化护套。",
      },
      {
        title: "墙体与 step 泛水维修",
        body: "恢复 step 和 counter-flashing 已无法导水的墙体与屋顶接口。",
      },
      {
        title: "烟囱及复杂接口工程",
        body: "处理需与覆盖层按正确顺序施工的多平面泛水组件。",
      },
    ],
    repairVsReplace: {
      repair: "当屋顶现场完好且失效集中在接口时，泛水维修适用。",
      replace: "若泛水须完全重新整合进已至末期的覆盖层，可能需要现场更换。",
    },
    scopeFactors: [
      "穿透点数量和类型",
      "墙体饰面接口",
      "金属材料兼容性",
      "烟囱和老虎窗周围的进场",
      "现有覆盖层状况",
      "是否需要打开相邻组件",
    ],
    process: [
      "描述漏水位置及周围可见细部",
      "在失效区域屋顶层面检查泛水和穿透点",
      "书面维修范围，标明每个待修正细部",
      "以兼容金属、护套和膜材重新细部处理",
      "复核已修正接口及周边覆盖层",
    ],
    publicPriceHint:
      "价格反映穿透点数量和类型、墙体接口复杂程度，以及需打开并重新整合的周边覆盖层范围。请申请评估。",
    faqs: [
      {
        question: "什么是泛水 / 屋顶穿透点维修？",
        answer:
          "泛水和屋顶穿透点维修修正屋顶覆盖层与墙体、烟囱、通风口、管道及其他穿透点之间的导水接口。金属件、护套或密封胶接缝失效时，这些细部是频繁的漏水来源。",
      },
      {
        question: "怎么知道漏水是泛水问题？",
        answer:
          "在烟囱、侧墙、天窗、管道立管或通风口附近出现的漏水，尤其在风雨天，通常与泛水有关。室内水渍与上方穿透点对齐是强烈信号，但仍需屋顶层面检查确认。",
      },
      {
        question: "不换屋顶能修泛水吗？",
        answer:
          "通常可以，当周边覆盖层仍可使用时。单个泛水、护套和套环可更换并重新编入现有屋顶。若现场已至末期，重新细部处理更适合与更换规划一并进行。",
      },
      {
        question: "为什么给泛水打胶没能止住漏水？",
        answer:
          "密封胶老化远快于金属，无法弥补错误的泛水几何形态。表面填胶常掩盖失效，而水仍继续进入。耐久维修须重建金属细部；密封胶只起次要作用。",
      },
      {
        question: "BC 沿海管道护套和泛水能用多久？",
        answer:
          "使用寿命因材料、暴露和安装质量而异，因此没有单一诚实数字。橡胶管道护套通常比金属泛水老化更快，因为紫外线和温度循环使弹性体硬化，沿海风雨也更早暴露薄弱细部。",
      },
    ],
  },
  "moss-removal-roof-gutter": {
    title: "除苔藓 + 屋顶/天沟套餐",
    shortTitle: "苔藓与天沟",
    eyebrow: "维护套餐",
    description:
      "为大温哥华住宅提供除苔藓、清理杂物、天沟清洁和目视屋顶检查，应对有机生长和堵塞排水加速屋顶磨损的问题。",
    definition:
      "除苔藓与屋顶/天沟维护套餐，处理屋面有机生长、清理天沟和落水管，并记录可见屋顶状况供规划参考。",
    forWhom: "瓦面或天沟有苔藓、地衣或大量杂物，尤其位于树木下方的业主。",
    warningSigns: [
      "苔藓层在瓦片上滞留水分",
      "普通降雨时天沟溢流",
      "朝北坡面有机生长",
      "天沟内杂物淤积",
      "落水管排水不畅",
    ],
    evaluates: [
      "苔藓和有机生长范围",
      "生长下方的覆盖层状况",
      "天沟和落水管排水",
      "天沟和边缘杂物",
      "是否已有需要维修的损坏",
    ],
    causes: [
      "阴湿沿海条件",
      "树木杂物",
      "天沟维护疏忽",
      "表面纹理滞留有机物",
      "凉爽月份屋面干燥不足",
    ],
    methodology: [
      "保护景观和进场通道",
      "以适合覆盖层的方法清除苔藓和杂物",
      "清洁天沟并冲洗落水管",
      "进行目视状况复核",
      "记录清洁过程中发现的维修优先事项",
    ],
    materials: ["适合屋顶类型的柔和清洁工具", "使用时经批准的护理产品", "天沟清理和冲洗设备"],
    bcClimate:
      "低陆平原的阴凉和降雨为苔藓创造了理想条件。生长会困住覆盖层上的水分，若忽视可能加速颗粒脱落。",
    whenAppropriate: "当覆盖层基本完好，生长、杂物和排水是主要问题时。",
    whenOtherService: "瓦片缺失、主动漏水或泛水失效需要维修服务，而非仅清洁。",
    solutionCategories: [
      {
        title: "苔藓与有机物清除",
        body: "在适当的屋面上减少滞留水分的生长。",
      },
      {
        title: "天沟与落水管清洁",
        body: "恢复檐口和排放点的畅通排水。",
      },
      {
        title: "目视屋顶复核",
        body: "记录杂物清除后显露的损坏覆盖层。",
      },
    ],
    repairVsReplace: {
      repair: "当屋顶仍可使用时，清洁适合以生长和杂物为主的情况。",
      replace: "若清洁显示大范围脆化，诚实规划更换可能比反复清洁更合适。",
    },
    scopeFactors: [
      "屋顶面积",
      "坡度和进场",
      "树木覆盖密度",
      "天沟长度",
      "覆盖层脆弱程度",
      "杂物清运",
    ],
    process: [
      "说明屋顶类型、遮阴暴露和进场条件",
      "现场复核生长范围、排水和覆盖层状况",
      "书面范围：清洁、护理和天沟工作",
      "小心清除、在适当时护理并冲洗落水管",
      "附照片记录任何需要维修的事项",
    ],
    publicPriceHint:
      "价格取决于屋顶面积、坡度、生长程度、天沟长度和进场条件。请申请评估以获取针对具体项目的报价。",
    faqs: [
      {
        question: "什么是除苔藓 + 屋顶/天沟套餐？",
        answer:
          "除苔藓与屋顶/天沟维护套餐，处理屋面有机生长、清理天沟和落水管，并记录可见屋顶状况供规划参考。",
      },
      {
        question: "高压冲洗适合除屋顶苔藓吗？",
        answer:
          "高压冲洗通常不建议用于沥青瓦，因为会剥离保护颗粒并可能把水驱入覆盖层下方。苔藓更适合用较温和的机械方法，并在适当时使用适合屋面材料的护理产品。",
      },
      {
        question: "苔藓真的会损坏屋顶，还是只是外观问题？",
        answer:
          "成熟的苔藓会滞留水分、翘起瓦边并加速颗粒脱落。在雪松上会保持木材潮湿、加快腐朽。尽早清除远比修复其造成的磨损更经济。",
      },
      {
        question: "大温哥华屋顶应该多久除一次苔藓？",
        answer:
          "取决于遮阴、树木覆盖和屋面朝向：朝北和重度遮阴的坡面再生最快。许多本地住宅适合每年查看一次，在生长开始成片时清洁，而不是固定周期。",
      },
      {
        question: "除苔藓能止住天沟溢流吗？",
        answer:
          "两者常相关：苔藓碎屑和屋顶杂物是天沟和落水管堵塞的常见原因。此套餐同时清理两者，以便单独判断排水表现。若清洁后仍溢流，可能是金属或坡度问题。",
      },
    ],
  },
  "roof-health-check-maintenance": {
    title: "屋顶健康检查与维护",
    shortTitle: "健康检查",
    eyebrow: "先看状况",
    description: "检查、拍照、少量密封胶或维护工作，以及供规划参考的屋顶状况报告。",
    definition:
      "屋顶健康检查是对覆盖层、泛水、排水及可接近相关组件的结构化目视评估，通常配合照片记录和事先约定的有限维护项目。",
    forWhom: "准备买卖、计划翻修、注意到早期警示迹象，或希望在雨季前建立有记录基线的业主。",
    warningSigns: [
      "不确定屋顶年龄或剩余状况",
      "轻微水渍但来源不明",
      "季节性杂物和苔藓积聚",
      "穿透点密封胶老化",
      "需要有记录的计划基线",
    ],
    evaluates: [
      "按屋面划分的覆盖层状况",
      "泛水和穿透点",
      "排水和天沟接口",
      "可见通风组件",
      "维护与维修事项的优先排序",
    ],
    causes: [
      "正常老化但缺乏记录",
      "维护拖延",
      "场地特定的天气暴露",
      "质量不明的以往工程",
      "买卖或翻修决策需求",
    ],
    methodology: [
      "了解关注点和可获取的历史",
      "系统检查外部屋顶区域",
      "拍摄代表性状况照片",
      "仅完成约定的少量维护",
      "提供清晰的状况摘要和选项",
    ],
    materials: [
      "照片记录",
      "包含时的少量密封胶或紧固件",
      "若纳入范围则清理有限杂物",
      "书面状况说明",
    ],
    bcClimate: "季节性降雨使入冬前和暴风雨后的健康检查有助于发现堵塞排水、材料翘起和早期泛水失效。",
    whenAppropriate: "在承诺维修、更换或进一步调查前需要清晰了解状况时。",
    whenOtherService: "主动严重漏水或暴风雨开口应走紧急或维修路径。",
    solutionCategories: [
      {
        title: "有记录的检查",
        body: "对屋顶组件上可见且可接近部分的结构化审阅。",
      },
      {
        title: "少量维护",
        body: "仅在纳入范围且适当时进行的有限密封胶或杂物工作。",
      },
      {
        title: "规划摘要",
        body: "区分需立即处理的事项与中期监测事项。",
      },
    ],
    repairVsReplace: {
      repair: "许多发现是维护或局部维修事项，而非全面更换的触发因素。",
      replace: "仅当观察到的状况支持更广泛路径时才讨论更换。",
    },
    scopeFactors: [
      "屋顶大小和复杂程度",
      "进场条件",
      "照片记录深度",
      "包含的维护项目",
      "物业上的屋顶系统数量",
    ],
    process: [
      "预约健康检查并说明任何关注点",
      "系统性外部检查并拍照记录",
      "上门期间完成约定的少量维护",
      "书面状况摘要及排序后的优先事项",
      "就建议的维修进行后续讨论",
    ],
    publicPriceHint:
      "健康检查范围取决于屋顶大小和复杂程度、进场条件，以及包含多少记录和少量维护。请申请评估。",
    faqs: [
      {
        question: "什么是屋顶健康检查与维护？",
        answer:
          "屋顶健康检查是对覆盖层、泛水、排水及可接近相关组件的结构化目视评估，通常配合照片记录和事先约定的有限维护项目。",
      },
      {
        question: "我应该多久检查一次屋顶？",
        answer:
          "当地较有用的节奏是秋季降雨前检查一次，并在重大风暴后再查。树木覆盖重的住宅，或屋顶已进入预期寿命后半段的住宅，值得更频繁关注。",
      },
      {
        question: "健康检查后我会收到什么？",
        answer:
          "附有照片的覆盖层、泛水、排水和通风状况摘要，区分需及时维修的事项、需监测的事项和正常磨损。这是一份可按自己节奏采取行动的规划文件。",
      },
      {
        question: "买卖房屋前做屋顶检查值得吗？",
        answer:
          "值得：屋顶是较昂贵的建筑组件之一，有记录的状况基线能减少谈判中的猜测。请注意这是屋顶状况审阅，而非正式的购房验房。",
      },
      {
        question: "检查时发现的问题会当场修吗？",
        answer:
          "事先约定的小维护：例如穿透点少量密封胶更新，可在上门时完成。较大发现会记录并单独确定范围，让您凭清晰信息做决定，而非现场施压。",
      },
    ],
  },
  "shingle-spot-repair": {
    title: "沥青瓦局部维修",
    shortTitle: "瓦片局部维修",
    eyebrow: "局部覆盖层维修",
    description: "在周边现场仍可使用时，维修缺失、风损、开裂或其他损坏的沥青瓦局部区域。",
    definition:
      "沥青瓦局部维修在匹配条件和周边状况允许时，更换或重新固定有限区域的损坏沥青瓦，而无需全面重铺。",
    forWhom: "陡坡屋顶上出现孤立瓦片损坏、但整体仍完好的业主。",
    warningSigns: [
      "风后瓦片缺失",
      "瓦片开裂",
      "局部暴露紧固件或垫层",
      "某一区域颗粒堆积",
      "以往补丁翘起",
    ],
    evaluates: [
      "瓦片可匹配性",
      "周边现场柔韧性",
      "局部垫层状况",
      "损坏屋面的暴露程度",
      "损坏是否真正孤立",
    ],
    causes: ["风掀", "撞击损坏", "热裂", "踩踏损坏", "密封条老化失效"],
    methodology: [
      "确认可匹配性",
      "小心拆除损坏瓦片",
      "在需要时局部更换垫层",
      "以正确紧固安装匹配瓦片",
      "仅按细部要求密封",
    ],
    materials: ["匹配的沥青瓦", "局部垫层", "兼容紧固件", "仅在指定位置的细部密封胶"],
    bcClimate: "沿海风灾和潮湿冬季使及时局部维修在水到达屋面板之前尤为重要。",
    whenAppropriate: "当损坏局限且现场可接受兼容瓦片时。",
    whenOtherService: "大范围脆化或多屋面失效指向全面沥青瓦更换。",
    solutionCategories: [
      {
        title: "局部瓦片更换",
        body: "以兼容材料恢复局限损坏区域。",
      },
      {
        title: "边缘和屋脊修补",
        body: "在仍属孤立时修正高应力边缘的离散失效。",
      },
      {
        title: "漏水路径核查",
        body: "确认局部损坏未掩盖泛水失效。",
      },
    ],
    repairVsReplace: {
      repair: "现场仍柔韧且可匹配时进行局部维修。",
      replace: "磨损具有系统性时全面更换。",
    },
    scopeFactors: ["损坏面积", "坡度和进场", "材料匹配", "垫层需求", "天气窗口"],
    process: [
      "若安全可见，发送损坏区域照片",
      "在屋顶上确认损坏确实局限",
      "核对瓦片轮廓和颜色匹配",
      "小心拆除并更换受影响瓦片",
      "复核维修及周边现场",
    ],
    publicPriceHint: "局部维修价格取决于损坏面积、坡度和进场，以及替换瓦片的匹配程度。请申请评估。",
    faqs: [
      {
        question: "什么是沥青瓦局部维修？",
        answer:
          "沥青瓦局部维修在匹配条件和周边状况允许时，更换或重新固定有限区域的损坏沥青瓦，而无需全面重铺。",
      },
      {
        question: "暴风雨吹掉几片瓦，紧急吗？",
        answer:
          "应尽快处理。缺失瓦片会暴露并非为长期耐候设计的垫层，下一场大雨可能到达屋面板。快速局部维修通常可避免更大问题。",
      },
      {
        question: "新瓦能和现有屋顶颜色匹配吗？",
        answer:
          "新瓦很少与风化瓦完美匹配，因为现有瓦会褪色和掉粒。选择颜色相近的产品可减小对比，开工前会诚实讨论匹配预期。",
      },
      {
        question: "多少片损坏瓦片就不适合局部维修了？",
        answer:
          "没有固定数量：关键在损坏是否局限、周边现场是否仍足够柔韧以接受施工而不开裂。多个屋面反复失效通常表明更换规划更经济。",
      },
      {
        question: "开裂瓦片能只打胶而不换吗？",
        answer:
          "开裂瓦片表面打胶寿命短，且可能使日后维修更复杂。更换损坏瓦片可恢复预期的导水搭接，通常是更耐久、长期更省的做法。",
      },
    ],
  },
  "gutter-cleaning-downspout-flush": {
    title: "天沟清洁 + 落水管冲洗",
    shortTitle: "天沟清洁",
    eyebrow: "排水维护",
    description: "清洁天沟并冲洗落水管，使檐口排水在大温哥华降雨中正常工作。",
    definition:
      "天沟清洁清除天沟和落水管中的杂物，使屋顶径流能沿檐口离开，而不会溢入封檐板、地基或景观。",
    forWhom: "看到溢流、天沟内植物生长，或普通降雨后封檐板出现水渍的业主。",
    warningSigns: [
      "水从天沟外壁流下",
      "槽内长出植物",
      "地基溅水",
      "封檐板水渍",
      "测试时落水管不排水",
    ],
    evaluates: ["杂物量", "槽体坡度", "出水口和延伸管状况", "封檐板固定", "金属是否已失效"],
    causes: ["落叶和苔藓杂物", "针叶树针叶积聚", "坡度沉降", "滤网堵塞", "错过季节性清洁"],
    methodology: [
      "清理槽体",
      "冲洗落水管",
      "检查出水口和延伸管",
      "记录金属状况",
      "仅在合理时建议滤网或更换",
    ],
    materials: ["清洁工具", "冲洗设备", "仅在纳入范围的基本紧固件"],
    bcClimate: "频繁降雨意味着堵塞的天沟会迅速浸湿大温地区的封檐板和地基。",
    whenAppropriate: "当金属仍完好且杂物是主要问题时。",
    whenOtherService: "生锈、分离或长期尺寸不足的天沟可能需要更换。",
    solutionCategories: [
      {
        title: "槽体清洁",
        body: "清除天沟中的有机和颗粒杂物。",
      },
      {
        title: "落水管冲洗",
        body: "恢复连续排放路径。",
      },
      {
        title: "状况记录",
        body: "标注清洁过程中发现的失效金属或封檐板问题。",
      },
    ],
    repairVsReplace: {
      repair: "槽体结构仍完好时进行清洁。",
      replace: "清洁后金属或坡度仍无法排水时需更换。",
    },
    scopeFactors: ["线性长度", "层数和进场", "杂物类型", "落水管数量", "安全要求"],
    process: [
      "说明房屋高度、天沟长度和进场条件",
      "清除落叶、针叶和颗粒杂物",
      "冲洗每条落水管并确认畅通排放",
      "作业时记录金属、坡度和封檐板状况",
      "报告仅靠清洁无法解决的问题",
    ],
    publicPriceHint: "价格通常取决于线性长度、层数、杂物量以及安全和进场要求。请申请评估。",
    faqs: [
      {
        question: "什么是天沟清洁 + 落水管冲洗？",
        answer:
          "天沟清洁清除天沟和落水管中的杂物，使屋顶径流能沿檐口离开，而不会溢入封檐板、地基或景观。",
      },
      {
        question: "大温哥华天沟应该多久清洁一次？",
        answer:
          "大多数本地住宅至少需要秋季落叶后清洁一次；针叶树下的物业常需第二次，因为针叶全年积聚。普通降雨时溢流说明已等太久。",
      },
      {
        question: "跳过天沟清洁会怎样？",
        answer:
          "堵塞的天沟会溢向封檐板和外墙、在地基处倒水，并可能把水带回屋顶边缘下方。在此气候下，小维护任务常会演变成封檐板腐烂或排水维修。",
      },
      {
        question: "你们会冲洗落水管还是只清天沟？",
        answer:
          "两者都做。槽体干净但落入堵塞落水管仍会溢流。每条落水管都会冲洗并确认排放，因为下弯和地下堵塞是最常见的隐蔽失效。",
      },
      {
        question: "天沟干净仍溢流，怎么办？",
        answer:
          "这指向坡度、尺寸或出水口问题而非杂物。清洁上门包含状况记录；若金属或几何形态无法排水，会说明理由并建议维修或更换。",
      },
    ],
  },
  "storm-tarping-emergency-dry-in": {
    title: "暴风雨防水布 / 紧急临时防水",
    shortTitle: "暴风雨防水",
    eyebrow: "临时防水",
    description: "在风、树或暴风雨损坏后、永久屋顶工程进行前的临时防水。",
    definition:
      "暴风雨防水布和紧急临时防水在永久维修规划期间，临时保护开口或损坏屋面区域免受天气影响，以限制室内损坏。",
    forWhom: "暴风雨后出现裸露屋面板、树木撞击或大面积缺失现场的业主。",
    warningSigns: [
      "树木撞击后屋面板裸露",
      "大面积现场缺失",
      "新开口处室内漏雨",
      "屋顶上有不稳定杂物",
      "永久材料到位前需要临时保护",
    ],
    evaluates: ["安全和进场", "开口大小和位置", "室内风险", "天气窗口", "通往永久维修的路径"],
    causes: ["暴风雨", "树木倒塌", "覆盖层突然缺失", "撞击损坏", "材料部分老化失效"],
    methodology: [
      "优先进行安全评估",
      "仅为安全防水清除必要杂物",
      "安装临时防水",
      "记录状况",
      "规划永久维修路径",
    ],
    materials: ["防水布和临时膜材", "适合临时作业的可靠固定", "开口和边缘保护"],
    bcClimate: "太平洋风暴可迅速打开屋顶；临时防水可在确定永久范围前限制二次室内损坏。",
    whenAppropriate: "在永久工程前必须对开口或不稳定区域防雨时。",
    whenOtherService: "临时防水不能替代损坏组件的永久维修或更换。",
    solutionCategories: [
      {
        title: "临时防水",
        body: "限制暴风雨开口处的天气进水。",
      },
      {
        title: "稳定处理",
        body: "固定可能引发进一步损坏的松散材料。",
      },
      {
        title: "状况记录",
        body: "记录状况以支持永久维修规划。",
      },
    ],
    repairVsReplace: {
      repair: "无法立即完成永久工程时先进行临时防水。",
      replace: "组件可正确恢复后跟进永久维修或更换。",
    },
    scopeFactors: ["开口大小", "进场和高度", "天气", "室内暴露", "杂物危险"],
    process: [
      "联系并提供照片及安全细节（如有）",
      "现场危险和进场评估",
      "仅为安全覆盖稳定必要杂物",
      "在开口上固定临时防水",
      "记录状况并规划永久维修",
    ],
    publicPriceHint: "临时防水范围取决于开口大小、屋顶高度、施工时天气及杂物危险。请申请评估。",
    faqs: [
      {
        question: "什么是暴风雨防水布 / 紧急临时防水？",
        answer:
          "暴风雨防水布和紧急临时防水在永久维修规划期间，临时保护开口或损坏屋面区域免受天气影响，以限制室内损坏。",
      },
      {
        question: "树砸到屋顶，我先做什么？",
        answer:
          "让人员远离损坏下方房间，不要上屋顶，从地面和室内拍照记录。然后安排专业临时防水：不稳定树枝和隐蔽结构损坏使暴风雨损坏的屋顶很危险。",
      },
      {
        question: "屋顶防水布能盖多久？",
        answer:
          "防水布是临时防雨，不是屋面。实际寿命以周至数月计，取决于暴露、固定和冬季风暴。应在条件和材料允许时尽快安排永久维修。",
      },
      {
        question: "保险会赔紧急防水布吗？",
        answer:
          "许多物业保单将突发损坏后合理的临时保护视为可赔减损措施，但保障因保单而异。Revolin 会记录状况和已完成工作以支持理赔，但无法就您的保单条款提供建议。",
      },
      {
        question: "临时防水能代替修屋顶吗？",
        answer:
          "不能。临时防水在规划正确维修或更换时限制室内损坏。长期留置通常因固定和材料退化而导致渐进进水。",
      },
    ],
  },
  "skylight-leak-flashing-repair": {
    title: "天窗漏水 / 泛水维修",
    shortTitle: "天窗维修",
    eyebrow: "天窗接口",
    description: "维修或重新泛水漏水天窗及周围屋面。",
    definition: "天窗漏水和泛水维修处理天窗基座、泛水及相邻屋面的进水，而不假设玻璃本身是原因。",
    forWhom: "天窗下方有水渍、基座泛水失效，或不确定是冷凝还是真正漏水的业主。",
    warningSigns: [
      "天窗井下方水渍",
      "光井油漆剥落",
      "基座角部漏光",
      "单元旁 step flashing 失效",
      "低坡屋面上基座旁积水",
    ],
    evaluates: [
      "天窗类型和年龄",
      "基座和泛水状况",
      "相邻覆盖层整合",
      "室内冷凝模式",
      "是否需要更换单元",
    ],
    causes: [
      "基座泛水失效",
      "与瓦片或膜材整合不当",
      "密封老化",
      "冷凝被误判为漏水",
      "框架开口位移",
    ],
    methodology: [
      "在可能时区分漏水与冷凝",
      "检查基座泛水和相邻屋面",
      "以兼容细部重新泛水或维修",
      "在需要时处理周边覆盖层",
      "仅在合理时建议更换单元",
    ],
    materials: ["基座泛水", "兼容的垫层或膜材细部", "周边瓦片或膜材补丁", "密封胶仅作次要措施"],
    bcClimate: "风雨交加时，沿海 BC 的薄弱天窗基座很快暴露；仅靠玻璃状况很少能解释所有漏水。",
    whenAppropriate: "当天窗单元仍可使用且失效在泛水或相邻屋面时。",
    whenOtherService: "框架失效、长期漏水的密封单元或更大范围屋顶失效可能需要不同范围。",
    solutionCategories: [
      {
        title: "基座重新泛水",
        body: "恢复天窗基座处的金属和膜材接口。",
      },
      {
        title: "相邻覆盖层维修",
        body: "修正不再与单元整合的瓦片或膜材。",
      },
      {
        title: "漏水与冷凝复核",
        body: "帮助区分组件漏水与室内潮湿症状。",
      },
    ],
    repairVsReplace: {
      repair: "当单元和基座可接受耐久重新细部处理时进行维修。",
      replace: "当组件无法可靠恢复时更换单元或屋面。",
    },
    scopeFactors: [
      "天窗数量和尺寸",
      "屋顶系统类型",
      "基座高度和状况",
      "进场条件",
      "有风险的室内饰面",
    ],
    process: [
      "描述水渍模式及已知天窗年龄",
      "检查基座、泛水和相邻屋面",
      "区分漏水证据与冷凝",
      "重新泛水或维修失效细部",
      "复核接口并记录工作",
    ],
    publicPriceHint: "天窗工程按单元计价：基座状况、屋顶系统类型、泛水要求和进场条件。请申请评估。",
    faqs: [
      {
        question: "什么是天窗漏水 / 泛水维修？",
        answer: "天窗漏水和泛水维修处理天窗基座、泛水及相邻屋面的进水，而不假设玻璃本身是原因。",
      },
      {
        question: "是天窗漏水还是冷凝？",
        answer:
          "冷凝表现为玻璃和框架上的细滴或雾化，常见于寒冷天气和潮湿房间。漏水从基座角或上缘追踪，常污染光井石膏板。屋顶层面诊断可确认。",
      },
      {
        question: "漏水就意味着要换新天窗吗？",
        answer:
          "不一定。许多漏水源于泛水或周围屋面而非单元本身。若玻璃密封失效，夹层间持续雾化，或框架恶化，会讨论更换；否则重新泛水通常可解决。",
      },
      {
        question: "重铺屋顶时应更换天窗吗？",
        answer:
          "往往是合适时机：泛水无论如何都要重做，进场已具备，老化单元不太可能比周围新屋顶更耐用。状况良好的单元可保留并正确重新泛水。",
      },
      {
        question: "为什么只在风雨天漏？",
        answer:
          "风雨推动的雨水与垂直降雨对泛水细部负荷不同：可能爬上搭接层进入垂直降雨能很好导水的细部。该模式强烈指向泛水几何问题而非玻璃密封失效。",
      },
    ],
  },
  "chimney-flashing-repair": {
    title: "烟囱泛水维修",
    shortTitle: "烟囱泛水",
    eyebrow: "烟囱接口",
    description: "大温哥华屋顶上 step flashing、counter-flashing 及烟囱相关漏水修正。",
    definition: "烟囱泛水维修恢复砖砌或框架烟囱与屋面交接处导水的金属接口。",
    forWhom: "烟囱角部漏水、counter-flashing 生锈，或分水脊（cricket）细部老化的业主。",
    warningSigns: [
      "烟囱角部水渍",
      "counter-flashing 锈迹",
      "砖缝问题伴屋顶下方漏水",
      "宽烟囱缺少 upslope cricket",
      "烟囱底部涂满沥青",
    ],
    evaluates: [
      "烟囱尺寸和几何形态",
      "step 和 counter-flashing 状况",
      "上游 cricket 需求",
      "砌体接口",
      "周边覆盖层状况",
    ],
    causes: [
      "step flashing 编织不当",
      "counter-flashing 从砌体脱离",
      "热胀冷缩",
      "缺少上游 cricket",
      "老化和腐蚀",
    ],
    methodology: [
      "检查烟囱各面和屋面交接",
      "仅在需要时打开以重建金属层次",
      "正确安装 step 和 counter-flashing",
      "在需要时增设或恢复 cricket 细部",
      "重新整合周边覆盖层",
    ],
    materials: [
      "step 和 counter-flashing",
      "需要时的 cricket 构架/覆盖材料",
      "兼容垫层",
      "砂浆或密封胶仅作适当次要细部",
    ],
    bcClimate: "烟囱角部是大温风雨交加时的高应力湿区；表面沥青不能长期替代金属层次。",
    whenAppropriate: "当烟囱和周边屋面现场可支持正确重新泛水时。",
    whenOtherService: "若屋面现场已至末期，重新泛水常与更换规划一并进行更合适。",
    solutionCategories: [
      {
        title: "step flashing 恢复",
        body: "与覆盖层重新建立编织金属层次。",
      },
      {
        title: "counter-flashing 修正",
        body: "恢复嵌入砌体或饰面细部的上部金属收口。",
      },
      {
        title: "cricket 与分水细部",
        body: "在需要时改善宽烟囱上游导水。",
      },
    ],
    repairVsReplace: {
      repair: "屋面现场仍可使用时进行烟囱重新泛水。",
      replace: "现场无法支持耐久整合时与重铺一并进行。",
    },
    scopeFactors: ["烟囱宽度", "屋顶坡度", "砌体状况", "进场条件", "周边覆盖层年龄"],
    process: [
      "分享烟囱照片及任何室内水渍",
      "检查烟囱各面和屋面交接",
      "书面范围：待重建的金属层次",
      "正确安装 step、counter 和 cricket 细部",
      "重新整合覆盖层并复核结果",
    ],
    publicPriceHint:
      "烟囱工程按烟囱宽度、屋顶坡度、砌体状况，以及需掀起并重新整合的周边覆盖层计价。请申请评估。",
    faqs: [
      {
        question: "什么是烟囱泛水维修？",
        answer: "烟囱泛水维修恢复砖砌或框架烟囱与屋面交接处导水的金属接口。",
      },
      {
        question: "屋顶是新的为什么烟囱还漏？",
        answer:
          "烟囱泛水是重铺后常见薄弱点：若 counter-flashing 复用或 step flashing 未正确重编，新覆盖层会把水直接导入旧失效。烟囱接口须作为独立组件细部处理。",
      },
      {
        question: "counter-flashing 是什么，为什么重要？",
        answer:
          "step flashing 将水带过瓦片；counter-flashing 是嵌入砌体、覆盖 step 竖向腿的上部金属层。没有完好的 counter-flashing 收口，沿烟囱面向下的水会直接进入 step 金属后方。",
      },
      {
        question: "烟囱后面需要 cricket 吗？",
        answer:
          "下游侧宽烟囱会在上游面积聚水和杂物。cricket 是小型分水屋脊，将水绕烟囱疏导。是否需要取决于烟囱宽度、屋顶坡度和观察到的状况。",
      },
      {
        question: "屋顶沥青能当烟囱泛水修复吗？",
        answer:
          "作紧急止漏可以减缓漏水，但作为维修会失败：会收缩开裂并困住水，同时掩盖下方金属失效。持久修正须重建金属层次。",
      },
    ],
  },
  "gutter-replacement": {
    title: "天沟更换",
    shortTitle: "天沟更换",
    eyebrow: "檐口排水系统",
    description: "无缝铝天沟和落水管更换，确保檐口排水可靠。",
    definition: "天沟更换安装新的天沟和落水管，按尺寸和坡度将屋顶径流带离建筑围护结构。",
    forWhom: "清洁后仍长期溢流、天沟生锈分离，或因排水失效导致封檐板损坏的业主。",
    warningSigns: [
      "接缝开裂",
      "天沟脱离封檐板",
      "清洁后仍长期溢流",
      "腐蚀穿孔",
      "原位无法纠正的错误坡度",
    ],
    evaluates: ["线性长度和层数", "出水口设计", "封檐板状况", "颜色和型材", "落水管走向"],
    causes: ["金属使用寿命末期", "槽体尺寸不足", "封檐板位移", "杂物负荷", "原始安装不当"],
    methodology: [
      "确认封檐板就绪",
      "规划坡度和出水口",
      "安装无缝槽体和落水管",
      "测试水流路径",
      "处理溅水和排放点",
    ],
    materials: ["无缝铝天沟", "落水管和弯头", "吊架和紧固件", "端盖和出水口"],
    bcClimate: "大降雨量使正确的天沟尺寸和坡度对大温住宅至关重要。",
    whenAppropriate: "现有天沟即使清洁和小修后仍无法排水时。",
    whenOtherService: "若槽体仍完好，清洁或滤网可能已足够。",
    solutionCategories: [
      {
        title: "无缝檐口系统",
        body: "坡度导向可控出水口的新槽体。",
      },
      {
        title: "落水管设计",
        body: "将排放引离地基和人行道。",
      },
      {
        title: "封檐板协调",
        body: "在需要时先处理松软封檐板再挂新金属。",
      },
    ],
    repairVsReplace: {
      repair: "金属或几何形态失效时更换。",
      replace: "系统仍完好时仅清洁或维修。",
    },
    scopeFactors: ["线性长度", "高度/进场", "出水口数量", "颜色选择", "封檐板维修"],
    process: [
      "测量槽体、出水口和落水管走向",
      "确认封檐板可支撑新吊架",
      "书面范围含型材和颜色选择",
      "现场成型并安装无缝槽体",
      "水流测试和排放复核",
    ],
    publicPriceHint:
      "更换价格取决于线性长度、层数和进场、出水口数量，以及是否需先维修封檐板。请申请评估。",
    faqs: [
      {
        question: "什么是天沟更换？",
        answer: "天沟更换安装新的天沟和落水管，按尺寸和坡度将屋顶径流带离建筑围护结构。",
      },
      {
        question: "无缝天沟是什么意思？",
        answer:
          "无缝天沟在现场滚压成与各段匹配的连续长度，接缝仅出现在转角和出水口。比每隔几英尺拼接的分段天沟更少未来漏点。",
      },
      {
        question: "换屋顶时要一起换天沟吗？",
        answer:
          "若两者都接近末期，一并安排可避免拆除时损坏新天沟，并协调滴水边缘与槽体位置。完好的天沟不必仅因换屋顶而更换。",
      },
      {
        question: "大温哥华住宅需要多大天沟？",
        answer:
          "尺寸取决于排入每段槽体的屋面面积、坡度和出水口布置，而非统一答案。当地降雨强度会迅速暴露尺寸不足或排水不足的槽体，因此按住宅评估布局。",
      },
      {
        question: "如何让落水管水远离地基？",
        answer:
          "排放规划朝向合适的排水：延伸管、溅水管理或现有地面排水，避免屋顶水倒在地下室旁。场地坡度和物业排水安排决定正确做法。",
      },
    ],
  },
  "gutter-guard-installation": {
    title: "天沟滤网安装",
    shortTitle: "天沟滤网",
    eyebrow: "防杂物保护",
    description: "为树木繁茂物业减少落叶、苔藓和杂物堵塞频率的防护系统。",
    definition: "天沟滤网安装在天沟上方增加防护系统，减少落叶和杂物积聚，同时仍允许水进入槽体。",
    forWhom: "需要频繁清洁、多层进场困难，或位于重度树木下方的业主。",
    warningSigns: [
      "每年需多次清洁",
      "多层进场困难",
      "针叶树杂物重",
      "清洁后不久落水管又堵",
      "维护进场受限",
    ],
    evaluates: ["先检查天沟结构状况", "杂物特征", "产品适用性", "屋顶边缘几何形态", "维护预期"],
    causes: ["密集绿化", "屋顶几何形态将杂物导入槽体", "细杂物穿透简单滤网", "维护间隔不一致"],
    methodology: [
      "确认天沟完好",
      "选择合适防护系统",
      "安装时注意进水与边缘",
      "测试水流",
      "说明仍需要的维护",
    ],
    materials: ["滤网系统组件", "与天沟兼容的紧固件", "所需边缘配件"],
    bcClimate:
      "沿海树木掉落针叶和苔藓碎屑，单靠不频繁的清洁难以应对；滤网可减少但无法消除所有维护。",
    whenAppropriate: "当天沟结构完好且杂物负荷是主要问题时。",
    whenOtherService: "失效天沟应在安装滤网前更换。",
    solutionCategories: [
      {
        title: "防杂物系统",
        body: "减少落叶和针叶进入槽体。",
      },
      {
        title: "兼容性检查",
        body: "仅在仍能正确排水的天沟上安装。",
      },
      {
        title: "维护预期",
        body: "说明滤网能减少什么、什么仍需定期关注。",
      },
    ],
    repairVsReplace: {
      repair: "在完好天沟上、杂物负荷高时安装滤网。",
      replace: "金属失效时先更换天沟。",
    },
    scopeFactors: ["线性长度", "产品类型", "屋顶杂物特征", "进场条件", "现有天沟状况"],
    process: [
      "评估天沟状况和物业杂物特征",
      "推荐适合周边树木的滤网类型",
      "书面范围含任何所需预清洁",
      "安装滤网并注意进水与边缘",
      "诚实说明剩余维护节奏",
    ],
    publicPriceHint:
      "滤网价格取决于产品类型、线性长度、屋顶边缘几何形态和现有天沟状况。请申请评估。",
    faqs: [
      {
        question: "什么是天沟滤网安装？",
        answer: "天沟滤网安装在天沟上方增加防护系统，减少落叶和杂物积聚，同时仍允许水进入槽体。",
      },
      {
        question: "装了滤网就不用再清洁天沟了吗？",
        answer:
          "不是。滤网可显著减少清洁频率，但不能消除维护：杂物会积在滤网上方，天沟仍会集中物料，出水口应定期检查。诚实说法是更少清洁，而非零维护。",
      },
      {
        question: "滤网对松针有效吗？",
        answer:
          "细针叶会穿透简单穿孔滤网。微孔产品在针叶树下表现更好，尽管细屑膜仍可能需偶尔刷除。产品选择从屋顶实际掉落物开始。",
      },
      {
        question: "能在现有天沟上装滤网吗？",
        answer:
          "可以，若天沟结构完好、坡度正确且固定牢靠。在失效天沟上装滤网会把底层问题锁住，因此先核实状况：有时需清洁和小调整。",
      },
      {
        question: "滤网会在大雨时出问题吗？",
        answer:
          "正确选择和安装的系统可通过当地雨量。匹配不当的滤网在强降雨时可能导致溢流，尤其在陡屋面檐口下方，因此屋顶坡度和檐口水速影响选择。",
      },
    ],
  },
  "roof-ventilation-upgrade": {
    title: "屋顶通风升级",
    shortTitle: "通风升级",
    eyebrow: "阁楼气流",
    description: "箱体通风口、ridge vent、檐口进气和阁楼气流修正，支持屋顶和阁楼性能。",
    definition: "屋顶通风升级改善进气和排气路径，使阁楼在现有平衡不足时更有效地管理湿气和热量。",
    forWhom: "有阁楼冷凝、瓦片老化不均、檐口（soffit）进气堵塞或排气不足的业主。",
    warningSigns: [
      "阁楼冷凝或结霜",
      "阁楼霉味",
      "瓦片老化不均",
      "檐口进气堵塞或被封",
      "阁楼过热加剧潮湿问题",
    ],
    evaluates: ["阁楼构造", "现有排气通风口", "进气路径连续性", "檐口保温布置", "屋顶几何限制"],
    causes: ["排气不足", "进气堵塞", "保温覆盖檐口", "厨卫风扇排入阁楼", "以往重铺未考虑通风设计"],
    methodology: [
      "评估进气和排气平衡",
      "在可行时清理或恢复檐口进气",
      "增设或修正排气组件",
      "避免无进气的过度排气",
      "在需要时协调保温和气密讨论",
    ],
    materials: [
      "按规格的屋脊、箱体或其他排气通风口",
      "檐口进气组件",
      "挡板或相关阁楼配件",
      "新穿透点的泛水",
    ],
    bcClimate: "凉爽潮湿的 BC 天气使阁楼湿气管理很重要；通风是该系统的一部分，不能治愈所有漏水。",
    whenAppropriate: "当气流失衡导致阁楼潮湿或加速覆盖层磨损时。",
    whenOtherService: "通风升级不能替代漏水维修，也不能解决所有冷凝来源。",
    solutionCategories: [
      {
        title: "排气升级",
        body: "在适当时增设或修正屋脊、箱体或其他排气组件。",
      },
      {
        title: "进气恢复",
        body: "在堵塞处重新打开或改善檐口进气路径。",
      },
      {
        title: "平衡复核",
        body: "为阁楼容积制定连贯的进排气策略。",
      },
    ],
    repairVsReplace: {
      repair: "有记录的气流失衡时升级通风。",
      replace: "不要把通风口当作防水维修的替代品。",
    },
    scopeFactors: ["阁楼容积", "现有通风类型", "进气可用性", "屋顶覆盖层类型", "进场条件"],
    process: [
      "复核阁楼和外部通风组件",
      "评估阁楼容积的进排气平衡",
      "书面范围：通风口、进气恢复和挡板",
      "安装并为新组件做泛水",
      "确认气流路径畅通",
    ],
    publicPriceHint:
      "通风项目按阁楼容积、现有通风类型、进气可用性和涉及的覆盖层确定范围。请申请评估。",
    faqs: [
      {
        question: "什么是屋顶通风升级？",
        answer: "屋顶通风升级改善进气和排气路径，使阁楼在现有平衡不足时更有效地管理湿气和热量。",
      },
      {
        question: "阁楼通风不良有哪些迹象？",
        answer:
          "冬季屋面板下侧冷凝或结霜、阁楼霉味、霉斑、钉尖生锈，以及不同坡面瓦片老化不均。在沿海 BC，主导症状往往是湿气而非夏季高温。",
      },
      {
        question: "多加屋顶通风口能治好阁楼冷凝吗？",
        answer:
          "单靠排气不够。没有匹配檐口进气的排气可能从室内抽气而非室外，有时使湿气更糟。厨卫风扇排入阁楼和居住空间漏气常常是真正原因，因此评估整条路径。",
      },
      {
        question: "ridge vent 和箱体通风口有什么区别？",
        answer:
          "ridge vent 沿屋脊连续布置，均匀排出阁楼空气；箱体通风口是靠近屋脊的离散单元。哪种适合取决于屋脊长度、几何形态和进气分布。",
      },
      {
        question: "更好通风能让瓦片更耐用吗？",
        answer:
          "平衡通风可减轻加速瓦片老化并可能导致屋面板问题的阁楼热湿负荷。这是诸多因素之一：暴露、材料质量和安装仍很重要，因此有助于延长寿命而非保证寿命。",
      },
    ],
  },
  "fascia-soffit-repair-replacement": {
    title: "封檐板 + 檐口底板维修 / 更换",
    shortTitle: "封檐板与檐口",
    eyebrow: "檐口构件",
    description:
      "维修或更换腐烂或损坏的封檐板（fascia）、檐口底板（soffit）及支撑天沟和边缘细部的檐口组件。",
    definition: "封檐板和檐口底板维修或更换恢复完成屋顶边缘、支撑天沟并有助于进气通风的檐口组件。",
    forWhom: "封檐板松软、檐口面板失效、天沟脱落或檐口有害虫通道的业主。",
    warningSigns: [
      "封檐板松软或腐烂",
      "檐口面板下垂",
      "天沟支架被拉出",
      "漆面失效且基材腐朽",
      "害虫利用的开口",
    ],
    evaluates: ["损坏线性范围", "材料选择", "檐口通风需求", "天沟重新固定计划", "油漆或饰面范围"],
    causes: [
      "天沟溢流浸湿木材",
      "边缘金属失效",
      "原始材料使用寿命末期",
      "通风湿气",
      "撞击或改建损坏",
    ],
    methodology: [
      "探查并标绘损坏长度",
      "拆除失效组件",
      "安装新封檐板/檐口材料",
      "按设计恢复通风开口",
      "协调天沟重新安装",
    ],
    materials: ["封檐板或包覆系统", "檐口面板", "紧固件和收边", "纳入时的油漆或饰面系统"],
    bcClimate: "多雨月份天沟长期溢流是大温封檐板腐朽的常见原因。",
    whenAppropriate: "檐口组件损坏但屋面现场仍可能完好时。",
    whenOtherService: "当天沟或屋顶边缘系统造成或将再次造成损坏时，与天沟或边缘工程一并进行。",
    solutionCategories: [
      {
        title: "封檐板恢复",
        body: "更换支撑天沟和边缘金属的腐朽板材。",
      },
      {
        title: "檐口维修",
        body: "恢复檐口底板和进气开口。",
      },
      {
        title: "檐口协调",
        body: "将天沟和边缘细部与新组件按顺序施工。",
      },
    ],
    repairVsReplace: {
      repair: "损坏局限时局部维修。",
      replace: "沿檐口连续腐朽时长段更换。",
    },
    scopeFactors: ["线性长度", "材料选择", "高度/进场", "饰面要求", "与天沟的衔接"],
    process: [
      "探查并标绘损坏檐口长度",
      "规划天沟拆卸和重新安装",
      "书面范围含材料和饰面选择",
      "拆除腐朽组件并安装新材料",
      "恢复进气开口并重新挂天沟",
    ],
    publicPriceHint:
      "檐口工程取决于线性长度、高度和进场、材料与饰面选择，以及天沟与维修的衔接。请申请评估。",
    faqs: [
      {
        question: "什么是封檐板 + 檐口底板维修 / 更换？",
        answer: "封檐板和檐口底板维修或更换恢复完成屋顶边缘、支撑天沟并有助于进气通风的檐口组件。",
      },
      {
        question: "封檐板为什么会腐烂？",
        answer:
          "当地常见顺序是天沟溢流或滴水边缘失效，在雨季反复浸湿板材。漆面一旦失效，木材持续潮湿，腐朽加速。修复水源与更换板材同样重要。",
      },
      {
        question: "能不拆天沟就换封檐板吗？",
        answer:
          "很少能做好。天沟通过封檐板固定，正确更换意味着拆卸并重新悬挂，也是纠正坡度和固定到完好新板材的机会。",
      },
      {
        question: "檐口底板是阁楼通风系统的一部分吗？",
        answer:
          "在大多数住宅中是的：檐口面板带有向阁楼供气的进气开口。更换时必须保留或恢复该进气；美容升级时堵住会悄然造成阁楼潮湿问题。",
      },
      {
        question: "木、铝还是复合封檐板？",
        answer:
          "木材传统且可维修但需饰面维护。包覆木材的铝耐候但细部不当会困住湿气。复合材料抗腐且维护较少。暴露、预算和建筑形态指导选择。",
      },
    ],
  },
  // NEEDS HUMAN REVIEW: cedar shake terminology and maintenance claims
  "cedar-shake-repair-restoration": {
    title: "雪松木瓦维修 / 修复",
    shortTitle: "雪松维修",
    eyebrow: "高端木屋顶",
    description: "更换损坏雪松木瓦，并清洁或修复高端木屋顶的局部区域。",
    definition: "雪松木瓦维修在更广泛的雪松屋顶仍值得维护时，恢复离散失效木瓦及相关细部。",
    forWhom: "雪松屋顶在有限区域有开裂、缺失或翘曲木瓦的业主。",
    warningSigns: [
      "单片木瓦开裂或缺失",
      "局部翘曲",
      "苔藓在木材上滞留水分",
      "紧固件锈迹伴离散失效",
      "低坡雪松段杂物淤积",
    ],
    evaluates: ["匹配木瓦可得性", "剩余现场状况", "清洁需求", "泛水接口", "是否更接近系统性更换"],
    causes: ["紫外线和干湿循环", "生物生长", "撞击", "以往维修不当", "仍值得维护的高端木屋顶老化"],
    methodology: [
      "采购匹配木瓦",
      "小心更换失效单元",
      "处理滞留水分的杂物和生长",
      "复核工作区泛水",
      "记录剩余寿命指标",
    ],
    materials: ["适当等级的雪松木瓦", "需要时的垫层补丁", "兼容紧固件", "适合木屋顶的清洁方法"],
    bcClimate: "BC 潮湿气候对木屋顶要求高；现场基本完好时，维护可延长使用寿命。",
    whenAppropriate: "失效局限且更广泛的雪松现场仍可维护时。",
    whenOtherService: "系统性翘曲、漏水和垫层失效指向全面雪松更换。",
    solutionCategories: [
      {
        title: "选择性木瓦更换",
        body: "在不放弃可维护现场的情况下恢复失效单元。",
      },
      {
        title: "局部清洁",
        body: "减少维修区滞留水分的杂物和生长。",
      },
      {
        title: "状况分诊",
        body: "区分可维护雪松与需讨论全面更换的屋顶。",
      },
    ],
    repairVsReplace: {
      repair: "现场仍值得维护时进行维修。",
      replace: "木屋顶磨损具有系统性时更换。",
    },
    scopeFactors: ["维修面积", "木瓦匹配", "进场条件", "清洁范围", "相邻泛水需求"],
    process: [
      "评估现场、生长和失效位置",
      "采购适当等级的匹配木瓦",
      "书面范围：维修和清洁区域",
      "更换失效木瓦并处理滞留水分杂物",
      "记录剩余寿命指标供规划",
    ],
    publicPriceHint: "雪松维修反映维修面积、木瓦匹配、进场、清洁范围及相邻泛水需求。请申请评估。",
    faqs: [
      {
        question: "什么是雪松木瓦维修 / 修复？",
        answer: "雪松木瓦维修在更广泛的雪松屋顶仍值得维护时，恢复离散失效木瓦及相关细部。",
      },
      {
        question: "雪松屋顶值得修还是该换？",
        answer:
          "决定因素是整个现场及下方垫层状况，而非最糟的几片木瓦。整体完好、排水良好的局部开裂适合维修；大范围翘曲、松软木瓦和跨屋面反复漏水适合更换规划。",
      },
      {
        question: "为什么有些木瓦翘曲开裂？",
        answer:
          "雪松随反复浸湿和干燥而移动。向阳坡干燥剧烈而开裂；阴凉潮湿坡保持湿润而翘曲或长苔藓。年龄、木瓦等级和原始固定影响出现早晚。",
      },
      {
        question: "能给雪松做处理延长寿命吗？",
        answer:
          "在此气候下，保持雪松无苔藓和杂物是价值最高的维护，因为困住的水分驱动腐朽。表面处理存在且个案讨论；它们补充良好排水和干燥，而非替代。",
      },
      {
        question: "维修区域能和现有雪松颜色匹配吗？",
        answer:
          "新雪松起初呈金色，经季节暴露逐渐变灰，因此起初可见差异、逐渐融合。外观要求高时，开工前会讨论维修位置和预期。",
      },
    ],
  },
  "flat-low-slope-roof-repair": {
    title: "平屋顶 / 低坡屋顶维修",
    shortTitle: "平屋顶维修",
    eyebrow: "低坡膜材",
    description: "平屋顶和低坡屋顶上 SBS、热熔、TPO/EPDM 补丁、泛水和穿透点维修。",
    definition:
      "平屋顶和低坡屋顶维修处理膜材接缝、泛水、穿透点和局部膜材失效，而不一定更换整个低坡组件。",
    forWhom: "低坡屋顶有积水渗漏、接缝失效、基座漏水或膜材开口的业主。",
    warningSigns: [
      "接缝张开",
      "穿孔痕迹",
      "pitch pan 或穿透口袋失效",
      "有渗漏的起泡",
      "排水口或泄水口无法控制水",
    ],
    evaluates: ["膜材类型", "补丁兼容性", "排水模式", "穿透点状况", "保温是否在局部维修之外受潮"],
    causes: ["膜材老化", "踩踏损坏", "积水应力", "女儿墙处泛水位移", "不兼容的以往补丁"],
    methodology: [
      "识别膜材系统",
      "确定兼容补丁方法",
      "维修泛水和穿透点",
      "在可行时改善局部排水细部",
      "若系统已耗尽则建议更换",
    ],
    materials: [
      "兼容膜材补丁",
      "泛水金属和配件",
      "穿透口袋或预制细部",
      "与系统相符的胶粘剂或热工工艺",
    ],
    bcClimate: "长湿季和积水风险使大温低坡屋顶的接缝和排水细部至关重要。",
    whenAppropriate: "失效局限且膜材现场仍可接受耐久补丁时。",
    whenOtherService: "保温饱和或系统性接缝失效通常意味着更换规划。",
    solutionCategories: [
      {
        title: "膜材补丁",
        body: "以兼容方法维修接缝、穿孔和局部现场损坏。",
      },
      {
        title: "穿透点和基座细部",
        body: "恢复屋顶开口处的防水。",
      },
      {
        title: "排水导向修正",
        body: "处理使水滞留于薄弱点的细部。",
      },
    ],
    repairVsReplace: {
      repair: "膜材大体仍可用时维修。",
      replace: "系统饱和或反复失效时更换。",
    },
    scopeFactors: ["膜材类型", "维修面积", "进场条件", "穿透点数量", "排水条件"],
    process: [
      "识别膜材系统和失效点",
      "损坏周边受潮和排水复核",
      "书面范围：兼容补丁和细部",
      "以系统相符方法施工",
      "复核排水表现并记录工作",
    ],
    publicPriceHint:
      "平屋顶维修价格取决于膜材类型、维修面积、穿透点数量、排水条件和进场。请申请评估。",
    faqs: [
      {
        question: "什么是平屋顶 / 低坡屋顶维修？",
        answer:
          "平屋顶和低坡屋顶维修处理膜材接缝、泛水、穿透点和局部膜材失效，而不一定更换整个低坡组件。",
      },
      {
        question: "为什么平屋顶补丁要看膜材类型？",
        answer:
          "每种膜材粘结方式不同：SBS 为热熔或胶粘改性沥青，TPO 为热焊，EPDM 依赖胶粘剂和胶带。错误材料或方法的补丁无法耐久粘结：误识别补丁是重复漏水的常见原因。",
      },
      {
        question: "平屋顶积水有问题吗？",
        answer:
          "雨后长时间滞留的水会应力接缝、加速膜材老化并放大小缺陷。部分原因可维修：堵塞排水口、局部低点，结构性坡度问题则指向更换级讨论。",
      },
      {
        question: "冬天能修平屋顶吗？",
        answer:
          "通常可以，但需调整方法：部分材料和胶粘剂有温度限制，表面须干燥。紧急稳定几乎总是可行；某些永久细部可能等待合适天气窗口。",
      },
      {
        question: "怎么知道膜下保温是否受潮？",
        answer:
          "症状包括持续室内潮湿、膜材起泡和脚下松软区域；确认可能需要湿度扫描或试切。受潮保温很重要，因为在其上补丁会困住湿气并继续恶化。",
      },
    ],
  },
  "metal-roof-repair-maintenance": {
    title: "金属屋顶维修 / 维护",
    shortTitle: "金属维修",
    eyebrow: "金属系统",
    description: "金属屋面系统的紧固件、泛水、密封胶、板材和穿透点维修。",
    definition:
      "金属屋顶维修和维护在保留可用板材的同时，修正现有金属屋顶的紧固件、泛水、密封胶、板材损坏和穿透点。",
    forWhom: "金属屋顶有松动紧固件、失效收口、穿透漏水或离散板材损坏的业主。",
    warningSigns: [
      "明钉系统螺钉退出",
      "泡沫收口失效",
      "穿透套环漏水",
      "细部密封胶开裂",
      "泛水处切边开始腐蚀",
    ],
    evaluates: ["板材型材", "涂层状况", "紧固件类型", "收口和泛水细部", "损坏是否系统性"],
    causes: ["热胀冷缩", "紧固件类型不当", "密封胶老化", "撞击凹痕", "沿海暴露影响"],
    methodology: [
      "识别系统类型",
      "在适当时修正紧固件和收口",
      "维修泛水和穿透点",
      "处理离散板材损坏",
      "在需要金属细部时避免仅靠表面填胶",
    ],
    materials: ["兼容紧固件和垫圈", "收口条", "泛水金属", "仅在适当时的补漆系统"],
    bcClimate: "沿海暴露和热胀冷缩使紧固件和泛水成为金属屋顶上反复出现的维护点。",
    whenAppropriate: "板材仍完好且问题在构件层面时。",
    whenOtherService: "系统性涂层失效或过时型材可能导致更换讨论。",
    solutionCategories: [
      {
        title: "紧固件和收口维护",
        body: "恢复防风雨机械细部。",
      },
      {
        title: "泛水和穿透点维修",
        body: "修正进水的接口。",
      },
      {
        title: "板材级修正",
        body: "在不放弃完好现场的情况下处理离散损坏。",
      },
    ],
    repairVsReplace: {
      repair: "金属现场仍可使用时维护与维修。",
      replace: "系统在经济或技术上无法再维护时更换。",
    },
    scopeFactors: ["系统类型", "进场条件", "细部数量", "涂层状况", "穿透点密度"],
    process: [
      "识别板材系统和固定方式",
      "检查紧固件、收口、泛水和穿透点",
      "书面范围区分维护与维修",
      "以兼容组件修正细部",
      "将屋顶作为工作系统复核",
    ],
    publicPriceHint:
      "金属屋顶工程按系统类型、需修正的细部数量、涂层状况和进场确定范围。请申请评估。",
    faqs: [
      {
        question: "什么是金属屋顶维修 / 维护？",
        answer:
          "金属屋顶维修和维护在保留可用板材的同时，修正现有金属屋顶的紧固件、泛水、密封胶、板材损坏和穿透点。",
      },
      {
        question: "为什么金属屋顶螺钉会退出？",
        answer:
          "明钉系统依靠垫圈螺钉随热胀冷缩移动；多年后可能松动且垫圈硬化。正确修正：有时用加大螺钉，是这些系统的正常维护，不代表屋顶已失效。",
      },
      {
        question: "金属屋顶通风管处漏水常见吗？",
        answer:
          "穿透点是金属屋顶最常见漏点。护套和套环比板材老化更快，须适应板材移动。以兼容细部更换护套通常可解决，无需动现场。",
      },
      {
        question: "凹进的金属板能修吗？",
        answer:
          "不损害涂层或接缝的外观凹痕常可保留。破坏涂层、打开接缝或积水的损坏值得板材级维修或更换受影响板，大多数型材都可行。",
      },
      {
        question: "直立锁边和明钉金属维护有何不同？",
        answer:
          "直立锁边在夹具上隐藏固定，日常垫圈维护基本不再需要；注意力转向泛水、穿透点和收口密封胶。明钉系统需定期检查紧固件。",
      },
    ],
  },
  "cedar-shake-roof-replacement": {
    title: "雪松木瓦屋顶更换",
    shortTitle: "雪松更换",
    eyebrow: "高端木重铺",
    description: "为有意保留木屋顶建筑特色的住宅提供完整高端雪松木瓦重铺。",
    definition:
      "雪松木瓦屋顶更换是使用雪松木瓦或指定木屋面组件的完整重铺，包括基层准备和高端木系统的正确细部。",
    forWhom: "当系统性雪松磨损使维修不再合理，但仍希望延续木屋顶特色的业主。",
    warningSigns: [
      "系统性翘曲和开裂",
      "跨屋面反复漏水",
      "雪松下方垫层失效",
      "对剩余寿命的买卖或规划顾虑",
      "建筑意图保留木屋顶",
    ],
    evaluates: ["木瓦等级和型材", "垫层策略", "通风和干燥", "防火及项目背景考量", "几何复杂程度"],
    causes: ["完整使用寿命结束", "长期潮湿", "多屋面风暴损失", "随时间发现的基层问题"],
    methodology: [
      "评估基层和组件需求",
      "确定木瓦系统和垫层方案",
      "拆除期间保护物业",
      "将现场和细部作为完整木屋顶组件安装",
      "说明雪松维护预期",
    ],
    materials: ["指定等级的雪松木瓦", "垫层系统", "与木屋顶兼容的泛水", "紧固件和屋脊细部"],
    bcClimate: "BC 的高端木屋顶需要诚实的维护预期；当维修跟不上系统性磨损时，更换重置组件。",
    whenAppropriate: "业主希望完整雪松系统且现有木屋顶已至合理服务末期时。",
    whenOtherService: "现场仍强健的局部失效可能仍适合雪松维修。",
    solutionCategories: [
      {
        title: "完整雪松现场安装",
        body: "将新木瓦作为连贯屋顶系统安装。",
      },
      {
        title: "基层和垫层准备",
        body: "处理木材下方的屋面板和导水层。",
      },
      {
        title: "木屋顶细部",
        body: "为雪松性能细部处理泛水和边缘。",
      },
    ],
    repairVsReplace: {
      repair: "仅局部时维修。",
      replace: "磨损系统性且仍选择木系统时全面雪松重铺。",
    },
    scopeFactors: ["屋顶面积", "木瓦规格", "基层维修", "进场条件", "项目复杂程度"],
    process: [
      "评估现有木屋顶和基层",
      "确定木瓦等级、垫层和细部",
      "书面范围和物业保护计划",
      "拆除、基层准备和安装",
      "完工复核和维护指导",
    ],
    publicPriceHint:
      "雪松重铺价格反映屋顶面积和几何形态、木瓦规格、基层维修和进场。请申请评估以获取书面工程范围。",
    faqs: [
      {
        question: "什么是雪松木瓦屋顶更换？",
        answer:
          "雪松木瓦屋顶更换是使用雪松木瓦或指定木屋面组件的完整重铺，包括基层准备和高端木系统的正确细部。",
      },
      {
        question: "BC 雪松屋顶能用多久？",
        answer:
          "使用寿命因木瓦等级、暴露、细部，尤其是维护而差异很大，单一数字会误导。在此地区建造和维护良好的雪松屋顶常见可服务数十年；忽视维护的则早得多。评估中会讨论您坡面和遮阴的合理预期。",
      },
      {
        question: "雪松木瓦和雪松木瓦片有什么区别？",
        answer:
          "木瓦（shake）劈制更厚，有高端木屋顶常见的纹理面；木瓦片（shingle）锯制更薄更均匀。选择影响外观、细部和成本，在规格阶段确定。",
      },
      {
        question: "雪松屋顶需要特殊垫层吗？",
        answer:
          "木屋顶需要既能导水又让雪松干燥的垫层策略，因为雪松无法呼吸时表现很差。层间垫层和适当的檐口保护是正确建造的典型要素。",
      },
      {
        question: "在大温哥华雪松仍是合理选择吗？",
        answer:
          "对合适的住宅仍是刻意的高端选择：建筑、社区特色和业主对维护的投入都很重要。潮湿气候需要诚实的保养预期；偏好最少维护的业主通常更适合其他高端系统。",
      },
    ],
  },
} satisfies Record<string, ServiceContent>;

export const serviceAreaContent = {
  vancouver: {
    intro:
      "温哥华住宅从紧凑型独立屋到带扩建、露台和低坡部分的建筑师设计住宅，各有不同。有效的屋顶评估应从建筑的实际几何形态出发，而不是套用统一系统。",
    localContext:
      "温哥华市内的项目可能适用与卑诗省其他地区不同的要求。Revolin 会在最终确定工程范围前，确认物业所属辖区及相关项目要求。",
    assessmentFocus: [
      "原屋顶、扩建部分和低坡区域之间的交接",
      "天沟、墙体交接、穿透点和周边泛水",
      "在紧凑城市地块内必须有效运作的排水路径",
    ],
    propertyContexts: ["独立屋", "经翻修及扩建的住宅", "混合坡度屋顶系统"],
    faqs: [
      {
        question: "温哥华屋顶项目与卑诗省其他地区遵循相同规定吗？",
        answer:
          "不一定。温哥华市有自己的建筑附例体系。适用要求取决于地址和工程范围，因此应在规划时确认，而不是根据区域清单推定。",
      },
      {
        question: "一栋住宅会需要不止一种屋顶做法吗？",
        answer:
          "会。扩建、门廊和屋顶露台可能带来与主屋顶不同的坡度和交接。评估时应将这些连接视为一个整体的水管理系统。",
      },
    ],
  },
  burnaby: {
    intro:
      "本拿比住宅社区包括平坦地块、山坡街道，以及经过扩建而变化的房屋。这种多样性使排水、安全进场和屋顶各部分的连接成为初次评估的重要内容。",
    localContext:
      "由于暴露条件、树木覆盖、坡度和建筑形态因地而异，本拿比相隔几个社区的屋顶表现也可能不同。建议以物业现场观察到的状况为依据。",
    assessmentFocus: [
      "住宅周围的坡度、进场及材料搬运限制",
      "天沟、檐口、檐沟和低位屋面的排水",
      "扩建部分与原建筑相接的位置",
    ],
    propertyContexts: ["山坡物业", "带扩建部分的住宅", "成熟住宅区地块"],
    faqs: [
      {
        question: "为什么现场进场条件会影响屋顶工程范围？",
        answer:
          "进场条件影响保护措施、材料搬运和施工顺序。应在最终确定安装计划和工程范围前进行检查。",
      },
      {
        question: "檐沟应与屋顶一并检查吗？",
        answer:
          "排水应视为从屋面到合适排放点的连续路径。评估会记录可见的檐沟和落水管状况，但不会假定已包含可选的排水工程。",
      },
    ],
  },
  coquitlam: {
    intro:
      "高贵林物业可能同时具有坡地、多屋面住宅和邻近植被。建议维修或更换前，会综合检查屋顶状况、杂物路径、排水及风雨暴露。",
    localContext:
      "高贵林不同社区的海拔和暴露条件可能变化。合适的屋顶系统和细部因此取决于具体建筑，而不是全市统一的材料规定。",
    assessmentFocus: [
      "多层住宅由上层到下层屋面的排水",
      "复杂屋顶形态的天沟和屋顶与墙体交接",
      "檐沟、穿透点和排水位置周边的杂物积聚",
    ],
    propertyContexts: ["多层住宅", "坡地住宅", "邻近成熟植被的住宅"],
    faqs: [
      {
        question: "复杂屋顶一定需要更换吗？",
        answer:
          "不一定。复杂程度会影响诊断和细部处理，但维修还是更换仍取决于观察到的问题是局部还是系统性的。",
      },
      {
        question: "检查时如何考虑树木杂物？",
        answer: "评估可记录杂物积聚或妨碍排水的位置，再根据物业现场可见情况提出维护建议。",
      },
    ],
  },
  surrey: {
    intro:
      "素里涵盖多种住宅环境，从成熟社区到拥有大型复杂屋顶的新住宅。屋顶面积、暴露条件和交接部位数量都会影响实际工程范围。",
    localContext:
      "由于全市状况各异，不能将一处素里物业的建议当作另一处的标准规格。Revolin 会记录现场屋顶状况并说明取舍。",
    assessmentFocus: [
      "大型屋面以及各屋面之间的排水方式",
      "老虎窗、墙体、烟囱和穿透点处的泛水",
      "损坏是局部还是在屋顶系统内重复出现",
    ],
    propertyContexts: ["大型独立屋", "较新的多屋面屋顶", "成熟社区住宅"],
    faqs: [
      {
        question: "大型屋顶维修还是更换由什么决定？",
        answer:
          "面积本身不能决定。决定因素包括覆盖层状况、失效点的数量和位置、交接细部，以及能否实现耐久的局部维修。",
      },
      {
        question: "评估可以只限于一个问题区域吗？",
        answer:
          "我们会重点检查所报告的问题，但也应查看相邻屋面和排水路径，因为水可能移动后才在室内显现。",
      },
    ],
  },
  richmond: {
    intro:
      "列治文整体地势较低，长时间降雨时，有序的屋顶排水尤其明显。低坡区域、周边边缘和排放路径应与主要屋面覆盖层得到同等重视。",
    localContext:
      "沿海暴露和场地开阔程度会影响风雨到达屋顶边缘和交接部位的方式。只有检查住宅坡度、形态和暴露条件后，才会提出材料和细部建议。",
    assessmentFocus: [
      "低坡区域的有效排水和可见低点",
      "暴露于风雨的屋顶边缘、泛水和交接部位",
      "檐沟和落水管如何承接各屋面的雨水",
    ],
    propertyContexts: ["低坡屋顶区域", "开阔住宅地块", "现代独立屋"],
    faqs: [
      {
        question: "看起来平坦的屋顶真的是平的吗？",
        answer:
          "低坡屋顶仍应将水导向排水位置。评估会查明设计排水路径，以及水未按预期流动的可见迹象。",
      },
      {
        question: "沿海环境是否意味着金属一定是正确选择？",
        answer:
          "不是。屋顶坡度、系统设计、产品适用性、细部和业主重点都很重要。不能仅凭位置选择材料。",
      },
    ],
  },
  "north-vancouver": {
    intro:
      "北温哥华住宅常位于陡峭、湿润且植被茂密的环境。确定屋顶工程范围前，需切实考虑安全进场、集中径流和排水点周边杂物。",
    localContext:
      "低海拔社区与靠近山地的物业条件可能不同。评估会记录具体场地的暴露和屋顶进场情况，而不是套用通用的北岸规格。",
    assessmentFocus: [
      "陡峭或高位屋面的安全进场",
      "不同屋顶层级之间的大流量排水路径",
      "阴影屋面周边的杂物及水分滞留",
    ],
    propertyContexts: ["陡坡地块", "错层住宅", "林木环绕的住宅物业"],
    faqs: [
      {
        question: "阴影较多的屋顶会需要不同的维护计划吗？",
        answer:
          "可能会。干燥条件和杂物积聚因朝向和树木覆盖而异，因此维护指导应以观察到的屋面状况为依据。",
      },
      {
        question: "陡坡地块如何影响项目？",
        answer:
          "它会影响进场、保护和施工顺序。这些限制会在最终确定工程范围前记录，而不会留到施工时才处理。",
      },
    ],
  },
  "west-vancouver": {
    intro:
      "西温哥华住宅经常采用复杂体量、大面积玻璃、露台，以及陡坡和低坡屋顶的组合。成功的屋顶工程必须解决各处交接，同时兼顾建筑设计。",
    localContext:
      "临海、山坡和林木环绕的物业，即使在同一市区也可能有不同暴露条件。Revolin 会先评估具体屋顶几何形态、进场和水流路径，再建议系统。",
    assessmentFocus: [
      "建筑金属、膜材和坡屋顶区域之间的连接",
      "女儿墙、屋顶露台、墙体接口和隐蔽排水",
      "已完成室外空间周边的进场和保护规划",
    ],
    propertyContexts: ["建筑师设计住宅", "山坡住宅", "混合材料屋顶系统"],
    faqs: [
      {
        question: "屋顶更换能保留住宅的建筑特色吗？",
        answer:
          "这应是建议的一部分。可在评估技术适用性的同时，考虑轮廓、颜色、边缘细部和可见交接。",
      },
      {
        question: "为什么要一并评估混合屋顶系统？",
        answer: "水会跨越不同屋面之间的边界。适用于一个表面的细部，仍必须可靠连接至下一个系统。",
      },
    ],
  },
  "new-westminster": {
    intro:
      "新威斯敏斯特有较老住宅、翻修住宅和紧凑住宅地块，多代屋顶工程可能在此相接。首要任务是了解现有系统以及后期改动与之连接的位置。",
    localContext:
      "房龄本身不能决定屋顶状况。Revolin 会查看覆盖层、泛水、交接和排水点的证据，再区分可维修细部与更广泛的系统问题。",
    assessmentFocus: [
      "屋面可见的过往多层维修或改动",
      "扩建、门廊和附属结构处的交接",
      "紧凑地块的材料进场和邻近物业保护",
    ],
    propertyContexts: ["较老的独立屋", "经翻修的住宅", "紧凑住宅地块"],
    faqs: [
      {
        question: "较老住宅一定需要整体重铺屋顶吗？",
        answer: "不一定。可见状况和系统性能比住宅本身的房龄更重要。作出决定前需要有记录的评估。",
      },
      {
        question: "如果屋顶有多处过往修补怎么办？",
        answer:
          "诊断会记录以往工程。同一区域反复维修可能指向未解决的交接问题，而互不相关的修补可能各有原因。",
      },
    ],
  },
  "port-coquitlam": {
    intro:
      "高贵林港住宅常将简洁坡屋顶与后期增加的车库、扩建或有顶户外空间组合。这些次要屋面会改变水流到天沟、墙体和檐沟的方式。",
    localContext:
      "扩建处附近出现漏水，不代表源头一定就在正上方。评估会先追踪屋面和交接部位，再界定维修区域。",
    assessmentFocus: [
      "主屋顶、车库和后期扩建之间的连接",
      "承接上层屋面排水的低位屋面",
      "屋顶与墙体交接处的穿透点和泛水",
    ],
    propertyContexts: ["带附属车库的住宅", "经扩建的独立屋", "多层级屋顶排水"],
    faqs: [
      {
        question: "为什么漏水会出现在远离源头的位置？",
        answer:
          "水可能沿屋面板、结构构件或屋顶交接处移动后才显现。诊断会追查可能路径，而不是把室内水渍当作源头。",
      },
      {
        question: "车库和门廊屋顶属于评估范围吗？",
        answer:
          "可以属于，尤其当它们与主屋顶相连或承接主屋顶排水时。所要求的评估范围应明确需要检查的区域。",
      },
    ],
  },
  "port-moody": {
    intro:
      "满地宝临海湾及林木环绕的住宅环境，会使同一屋顶不同朝向受到不同的湿气、杂物和暴露影响。检查记录应按屋面区分这些状况。",
    localContext:
      "屋顶朝向、附近植被和建筑高度可能与材料名称同样重要。Revolin 会先记录这些变量，再讨论维护、维修或更换方案。",
    assessmentFocus: [
      "边缘、墙体和穿透点处的风雨",
      "天沟和排水点的杂物积聚",
      "阴影和暴露屋面的不同风化模式",
    ],
    propertyContexts: ["面向海湾的物业", "林木环绕的地块", "多屋面独立屋"],
    faqs: [
      {
        question: "同一屋顶不同朝向会以不同速度老化吗？",
        answer:
          "会。日照、阴影、风和杂物暴露因朝向而异。状况报告应分别描述各屋面，而不是为整个屋顶指定单一状况。",
      },
      {
        question: "表面杂物能证明屋顶正在失效吗？",
        answer: "不能。它属于维护和排水观察，本身不是更换诊断。仍需评估下方覆盖层和细部。",
      },
    ],
  },
  "maple-ridge": {
    intro:
      "枫树岭包括较大住宅物业、林木社区和山麓地块，各处暴露和进场条件差异很大。屋顶及其周边条件会作为一个项目计划整体检查。",
    localContext:
      "较高或更暴露的地块，可能遇到与低处社区不同的条件。因此，系统选择和细部以物业为依据，而不是区域推定。",
    assessmentFocus: [
      "较大或坡地现场的进场、材料布置和保护",
      "成熟植被附近的排水和杂物管理",
      "屋脊、檐口和屋顶边缘的暴露",
    ],
    propertyContexts: ["较大住宅地块", "山麓社区", "林木环绕的物业"],
    faqs: [
      {
        question: "较大的物业会改变屋顶建议吗？",
        answer:
          "本身不会。它可能改变进场和项目物流，而屋顶系统建议仍取决于坡度、系统、暴露条件和状况。",
      },
      {
        question: "如何检查暴露程度较高的屋顶边缘？",
        answer:
          "评估会记录可见边缘状况、与固定相关的问题及交接细部，再将建议与所选系统和适用要求相联系。",
      },
    ],
  },
  delta: {
    intro:
      "三角洲的社区包括沿海、农业区边缘和内陆住宅环境。不同暴露条件意味着选择方案前，必须评估具体地块的风、排水和屋顶几何形态。",
    localContext:
      "不能只凭市区名称为措瓦森、拉德纳或北三角洲的物业提出建议。Revolin 会根据该地址实际观察到的建筑和条件确定工程范围。",
    assessmentFocus: [
      "檐口、屋脊和周边细部的风力暴露",
      "大面积屋面的排水能力和排放",
      "不同暴露立面上的材料状况",
    ],
    propertyContexts: ["沿海社区", "开阔住宅地块", "内陆独立屋"],
    faqs: [
      {
        question: "同一种屋顶系统适合三角洲所有地区吗？",
        answer: "没有自动适用全市的选择。坡度、暴露、建筑、排水和预算都会影响合适的系统。",
      },
      {
        question: "强风后会检查什么？",
        answer:
          "针对性评估可查看可接近的屋顶边缘、覆盖层、泛水和穿透点是否有可见变化。安全优先；业主不应爬上湿滑或受损屋顶。",
      },
    ],
  },
  langley: {
    intro:
      "兰里物业可能包括较大独立屋、宽阔屋面和多个相连结构。清晰的工程范围会区分各屋面，同时解决它们如何排水和连接。",
    localContext:
      "开阔地块与成熟林木社区有不同的维护和暴露考量。Revolin 会记录现场实际状况，再建议材料或维修界限。",
    assessmentFocus: [
      "宽阔屋面和较长排水路径",
      "住宅、车库和有顶户外区域之间的连接",
      "开阔暴露区域与树木遮蔽区域的差异",
    ],
    propertyContexts: ["较大独立屋", "带附属结构的住宅", "开阔住宅地块"],
    faqs: [
      {
        question: "项目可以按屋面分阶段进行吗？",
        answer:
          "有时可以，但前提是系统和交接部位允许设置耐久的停工点。工程范围应说明分阶段工作如何影响相连区域。",
      },
      {
        question: "宽阔屋面需要某一种特定材料吗？",
        answer:
          "不需要。几何形态只是因素之一。必须综合考虑坡度、系统设计、暴露、外观、维护重点和预算。",
      },
    ],
  },
  "white-rock": {
    intro:
      "白石镇住宅可能兼具临海暴露、现代屋顶形态、阳台和低坡区域。边缘、墙体交接和排水细部会结合建筑设计一并检查。",
    localContext:
      "沿海条件并不意味着某一种屋顶产品普遍正确。会结合坡度和业主重点，考虑产品适用性、系统设计和可见细部。",
    assessmentFocus: [
      "暴露于风雨的周边和交接细部",
      "女儿墙、基座和相邻墙体周边的低坡排水",
      "可见金属细部与所选系统的兼容性",
    ],
    propertyContexts: ["面海住宅", "现代住宅", "混合坡度屋顶设计"],
    faqs: [
      {
        question: "海洋暴露会自动排除某种屋顶材料吗？",
        answer:
          "不会自动排除。必须针对物业检查完整系统、产品限制、涂层或表面处理、固定方式和交接细部。",
      },
      {
        question: "为什么阳台和女儿墙连接很重要？",
        answer: "它们可能中断简单的排水，并引入墙体、基座或膜材交接。这些接口应纳入有记录的评估。",
      },
    ],
  },
} satisfies Record<ServiceAreaSlug, ServiceAreaContent>;

export const ui = {
  common: {
    home: "首页",
    services: "服务项目",
    serviceAreas: "服务区域",
    requestAssessment: "申请屋顶评估",
    exploreServices: "浏览屋顶服务",
    learnMore: "了解更多",
    nextStep: "下一步",
    questions: "问题",
    call: "致电",
  },
  serviceDetail: {
    breadcrumbLabel: "面包屑导航",
    whoFor: "适用对象",
    conditionFirstTitle: "先看状况，再选产品",
    warningSigns: "常见警示迹象",
    assessmentDiscipline: "严谨评估",
    evaluatesTitle: "Revolin 的评估内容",
    solutionCategories: "方案类别",
    solutionsTitle: "方案应依据屋顶，而不是套用脚本",
    repairMakesSense: "适合考虑维修的情况",
    replacementMakesSense: "适合考虑更换的情况",
    scope: "工程范围",
    scopeTitle: "影响工程的因素",
    scopeBody:
      "实际工程范围在评估后确定。Revolin Roofing 不公布统一价格、寿命或系统，因为这些取决于建筑和所选系统。",
    beforeDecide: "决定之前",
    assessmentTitle: "申请清晰的屋顶评估",
    assessmentBody: "请告诉我们您观察到的情况及物业位置。我们会利用这些信息安排合适的下一步沟通。",
  },
  serviceArea: {
    regions: { metroVancouver: "大温哥华", lowerMainland: "低陆平原" },
    roofingServicesSuffix: "屋顶服务",
    titlePrefix: "屋顶服务地点：",
    confirmedCommunity: "是已确认的服务社区。这是服务区域页面，并不表示当地设有分公司。",
    localContext: "当地屋顶环境",
    propertyDecides: "建议取决于具体物业",
    propertyContexts: "规划时考虑的物业类型",
    assessmentPriorities: "评估重点",
    attentionPrefix: "在此地值得重点关注的事项：",
    planningDisclaimer:
      "这是一般规划信息，并非远程诊断或工程意见。检查结果和适用要求将针对具体物业确认。",
    relatedServices: "相关服务",
    decisionsPrefix: "住宅屋顶决策：",
    relatedBody:
      "从问题和观察到的屋顶状况出发。合适的下一步可能是针对性维修、预防性评估或计划更换。",
    genericService: "屋顶服务",
    faqSuffix: "常见问题",
    faqTitle: "评估前的常见问题",
    requestAssessment: "申请评估",
    assessmentTitle: "从眼前的屋顶开始。",
    assessmentBodyPrefix:
      "请告诉我们该物业、屋顶区域及您注意到的情况。相关信息有助于确定评估重点，但不能替代现场检查：",
    contactOptions: "查看所有联系方式",
    serviceNames: {
      roofRepair: "屋顶维修",
      roofReplacement: "屋顶更换",
      asphaltShingles: "建筑型沥青瓦",
      metalRoofing: "金属屋顶",
      flatRoofing: "平屋顶与低坡屋顶",
      inspectionMaintenance: "屋顶检查与维护",
    },
  },
  servicesIndex: {
    eyebrow: "屋顶服务",
    title: "正确的工程范围始于正确的诊断",
    body: "了解 Revolin Roofing 的核心服务类别。每项服务都从建筑、屋顶系统及可观察状况出发，而不是预先设定产品建议。",
    exploreService: "了解服务",
    technicalContext: "技术背景",
    sourcesTitle: "应参考卑诗省的最新资料",
    roofingManual: "RCABC 屋顶施工规范手册",
    roofingManualBody: "现行屋顶施工参考资料；提供链接不表示 Revolin 是会员或提供 RoofStar 保证。",
    bcCodes: "卑诗省法规",
    bcCodesBody: "适用于相关辖区和项目范围的省级法规、修订及公告。",
    vancouverBylaw: "温哥华建筑附例",
    vancouverBylawBody: "温哥华的特定要求需与省级法规分开核查。",
  },
  areaIndex: {
    breadcrumbLabel: "面包屑导航",
    eyebrow: "大温哥华和低陆平原",
    title: "屋顶服务区域",
    body: "Revolin Roofing 为以下社区的住宅屋顶提供评估服务。每个项目均根据物业的屋顶形式、暴露条件、进场条件及适用的当地要求进行规划。",
    coverageEyebrow: "服务范围，而非分公司",
    coverageTitle: "提供当地背景，不虚构办公地点",
    coverageBody:
      "这些页面说明可能提供屋顶评估服务的地区，并不表示 Revolin Roofing 在每个城市都设有办公室。请提供物业地址，以确认项目服务范围。",
    nearbyEyebrow: "附近的社区？",
    nearbyTitle: "请联系我们确认项目服务范围。",
    contactCta: "联系 Revolin Roofing",
  },
  corePages: {
    about: {
      eyebrow: "Revolin Group 旗下公司",
      title: "以建筑公司的整体视角提供屋顶专业服务",
      body: "Revolin Roofing 是 Revolin Group 的专业屋顶部门，将母品牌克制的建筑审美、透明流程和严谨项目思维带入屋顶系统。",
      relationship: "品牌关系",
      relationshipTitle: "专注屋顶，着眼整栋建筑。",
      relationshipBody: [
        "屋顶不是孤立表面。它与墙体、外墙、天窗、通风、排水和室内系统相接。Revolin Roofing 会结合更广泛的建筑背景处理这些接口。",
        "这一视角有助于更清楚地诊断和界定工程范围。当状况超出屋顶工程时，仍需相应工程师、设计师或专业人员参与；整体视角有助于更早识别这些界限。",
      ],
      principles: "工作原则",
      principlesTitle: "在每个决策点保持清晰",
      principleItems: [
        {
          title: "先评估，再建议",
          body: "提出工程范围前，会综合考虑可见症状、可能源头及更广泛的系统状况。",
        },
        {
          title: "尊重建筑设计",
          body: "材料、颜色、轮廓、边缘和交接会影响建筑观感，而不只是排水表现。",
        },
        {
          title: "说明限制",
          body: "假设、排除项及需进一步检查的状况，都应在沟通和工程范围中明确说明。",
        },
      ],
      parentCompany: "母公司",
      parentTitle: "了解 Revolin Group",
      parentBody: "如需翻修、施工及更广泛的建筑工程，请访问母公司网站。",
      parentLink: "访问 RevolinGroup.com",
    },
    gallery: {
      eyebrow: "施工前后",
      title: "大温哥华屋顶施工前后对比",
      body: "探索 Revolin Roofing 为大温哥华与低陆平原业主打造的施工前后图库：从紧急漏水维修到沥青瓦、金属、雪松木瓦与平屋顶更换，每组图库呈现问题、诊断与完工后的屋顶背景。",
      notice:
        "可按服务、屋顶类型或城市筛选，找到最贴近您住宅的案例，然后申请屋顶评估以获得针对您物业的建议。",
      projectHeroBody:
        "探索 Revolin Roofing 在大温哥华的施工前后变化：从紧急漏水维修到沥青瓦、金属、雪松木瓦与平屋顶的完整更换。每组图库突出低陆平原业主常见的问题、诊断重点与完工后的屋顶效果。",
    },
    faq: {
      eyebrow: "业主指南",
      title: "不带销售话术的屋顶问题解答",
      body: "有关评估、维修、更换及屋顶系统的实用信息。针对具体项目的建议需在现场检查后提供。",
    },
    contact: {
      eyebrow: "联系 Revolin Roofing",
      title: "告诉我们您观察到的情况",
      body: "请提供物业位置、已知屋顶类型，以及您关注的问题或考虑中的项目。只有在已配置的客户咨询服务接受提交后，表单才会确认送达。",
      direct: "直接联系",
      basics: "从基本信息开始",
      phone: "电话",
      email: "电子邮件",
      serviceRegion: "服务区域",
      regionValue: "大温哥华和低陆平原",
    },
    legal: {
      legal: "法律信息",
      privacyTitle: "隐私政策",
      termsTitle: "网站使用条款",
      privacyBody: "网站咨询、归因数据和必要浏览器存储的处理方式。",
      termsBody: "使用 Revolin Roofing 网站及一般教育内容时适用的条款。",
      lastUpdated: "最后更新：2026 年 8 月 10 日。",
      privacySections: [
        {
          title: "您提供的信息",
          body: "提交咨询时，表单可能收集您的姓名、电话号码、电子邮件地址、邮政编码、所需服务、首选时间和留言。只有在配置获批的客户咨询递送服务后，才能提交。",
        },
        {
          title: "归因及技术信息",
          body: "网站可能在会话存储中保存 UTM 值和 Google 广告点击标识符等活动参数，以确定咨询来源。分析事件不得包含表单中输入的私人信息。",
        },
        {
          title: "信息用途",
          body: "咨询信息用于回复屋顶需求、了解项目需要并维护相关业务记录。本政策不表示或授权出售任何个人信息。",
        },
        {
          title: "服务提供商与保留",
          body: "获批的托管、分析和客户咨询递送提供商可能代表 Revolin 处理信息。信息保留和访问仅限于正当的业务、法律及安全需要。",
        },
        {
          title: "您的选择",
          body: "您可联系 Revolin Group，查询与咨询相关的个人信息。也可通过浏览器设置清除浏览器存储。",
        },
      ],
      termsSections: [
        {
          title: "一般信息",
          body: "网站内容仅供一般教育和规划参考，不构成工程意见、建筑法规认定、检查证书或针对具体项目的建议。",
        },
        {
          title: "项目工程范围",
          body: "屋顶建议、价格、工期、产品选择、保修条款和排除项，仅在已签署项目文件中载明时适用。网站示例不构成对特定物业的承诺。",
        },
        {
          title: "图片和项目记录",
          body: "网站图片与项目资料仅供一般规划参考。工程范围、保修与承诺仅在已签署的项目文件中载明时适用。",
        },
        {
          title: "外部链接",
          body: "外部网站链接仅供参考，其可用性、内容和隐私做法由各自运营方控制。",
        },
        {
          title: "可接受使用",
          body: "不得滥用网站、尝试未经授权的访问，或通过表单提交非法、欺骗性或恶意材料。",
        },
      ],
    },
    assessment: {
      title: "申请屋顶评估",
      body: "请提供物业位置，并简要说明屋顶或所关注的问题。",
    },
  },
} as const;

export const services = serviceContent;
export const serviceAreas = serviceAreaContent;

export default { services, serviceAreas, ui } as const;
